// Onglet QCM : la salle d'examen du tableau de service (lots B4 + B5).
//
// Huit vues, un seul monde : le registre des items, les quiz transversaux, les statistiques par notion, la fiche
// d'un item, la séance de QCM, les flashcards, l'active recall et le mini-cours. Ce module ne porte QUE le rendu
// et les gestes ; le barème, la maîtrise pondérée, la répétition espacée, les appels IA, le dictaphone et les
// brouillons restent dans src/legacy/qcm.js, qui les expose en un point : window.qcmInterne.
//
// Le module remplace, par leur nom, window.renderQcmTab et window.afterRenderQcm.
import { html, attr } from '../../core/html.js';
import { state } from '../../core/store.js';
import { ico } from '../icones.js';
import { jaugeMaitrise, motMaitrise, puceRang, puceScore, avancement } from '../components/qcm-jauge.js';
import { carteQuestion, correctionHtml, propositionsHtml, reponsePrete, zoneCalque, zoneAide, revueQuestion, questionDejaPassee } from '../components/qcm-question.js';
import { actions } from '../../core/actions.js';
import { carteFlash, notesCarte, enteteCarte } from '../components/qcm-carte.js';

function I() { return window.qcmInterne || {}; }
function pct(x) { return Math.round((x || 0) * 100); }
function pluriel(n, un, des) { return n + ' ' + (n > 1 ? des : un); }
function partArg() { return state.qcm.part == null ? '' : state.qcm.part; }
function nomPartie(n, part) { var p = I().partsFor(n); return (part != null && p) ? p[part] : null; }
// Sous-parties réunies le temps d'une séance : au moins deux cochées, sinon on travaille comme d'habitude.
function reunion() { var p = state.qcm.parts; return (p && p.length >= 2) ? p.slice() : null; }
// Portée des QCM et des flashcards : la liste des sous-parties réunies, ou comme partArg. L'active recall et le
// mini-cours gardent partArg : ils se font une sous-partie à la fois.
function porteeArg() { return reunion() || partArg(); }
// « Diagnostic + Traitement » pour des sous-parties réunies, sinon le nom de la sous-partie.
function nomPortee(n, part, parts) {
    if (!parts) return nomPartie(n, part);
    var p = I().partsFor(n);
    return p ? parts.map(function(k) { return p[k]; }).filter(Boolean).join(' + ') : null;
}
function mouvementReduit() { try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) { return false; } }

// ================================================================= pièces communes

// Le message de la page : un aimant qui porte son mot, une phrase, et de quoi le refermer. Jamais une fenêtre :
// il commente ce qui vient de se passer sur la page, il n'interrompt rien.
function noticeHtml() {
    var nt = state.qcm.notice; if (!nt) return '';
    var classe = nt.type === 'error' ? 'puce-danger' : nt.type === 'warn' ? 'puce-warn' : 'puce-ok';
    var mot = nt.type === 'error' ? 'Erreur' : nt.type === 'warn' ? 'À savoir' : 'Fait';
    return html`<div class="qcm-notice qcm-notice--${nt.type || 'ok'}" role="status">
        <span class="puce ${classe}">${mot}</span>
        <p>${nt.text}</p>
        <button type="button" class="ico-btn" data-action="qcmFermerNotice" aria-label="Fermer ce message">${ico('x', { taille: 18 })}</button>
    </div>`;
}

// Fonction IA sans abonnement : un seul rappel en haut de page, pas un cadenas sur chaque bouton.
function bandeauAbonnement() {
    if (!state.qcm.abonnementRequis || I().ia()) return '';
    return html`<div class="qcm-abo" role="note">
        ${ico('lock-simple', { taille: 20 })}
        <p>Les fonctions écrites par l'IA (flashcards, mini-cours, correction de l'active recall, QCM pédiatrie) demandent un abonnement. Les QCM, les fiches et les podcasts restent ouverts.</p>
        <a class="btn btn-secondaire btn-sm" href="${window.pageUrl ? window.pageUrl('abonnement') : 'abonnement'}">Voir l'abonnement</a>
    </div>`;
}

// Bouton d'une fonction IA : verrouillé, il mène à l'abonnement au lieu de mentir sur ce qu'il fait.
function boutonIA(opts) {
    if (!I().ia()) {
        return html`<a class="btn btn-secondaire btn-sm" href="${window.pageUrl ? window.pageUrl('abonnement') : 'abonnement'}" title="Fonction écrite par l'IA, réservée aux abonnés">${ico('lock-simple', { taille: 16 })}${opts.verrou || opts.label}</a>`;
    }
    var p = state.qcm.aiProgress;
    // « occupé » (l'appel est en cours) et « désactivé » (il n'y a rien à envoyer) ne disent pas la même chose :
    // un bouton grisé faute de matière ne doit pas annoncer une rédaction qui n'a pas commencé.
    var libelle = opts.occupe ? (opts.enCours || 'Rédaction') + (p && p.total > 1 ? ' ' + (p.fait + 1) + '/' + p.total + '…' : ' en cours…') : opts.label;
    var bloque = opts.occupe || opts.desactive;
    var aide = opts.desactive ? (opts.aideDesactive || '') : (opts.titre || '');
    return html`<button type="button" class="btn ${opts.classe || 'btn-primaire'} btn-sm" data-action="${opts.action}"${opts.args ? html` data-args="${attr(opts.args)}"` : ''}${bloque ? html` disabled aria-disabled="true"` : ''}${aide ? html` title="${aide}"` : ''}>${opts.occupe ? '' : ico('sparkle', { taille: 16 })}${libelle}</button>`;
}

function retourItem(n, texte) {
    return html`<button type="button" class="qcm-retour" data-action="qcmOpenItem" data-args="${attr([String(n), porteeArg(), '', I().pediaArg()])}">${ico('arrow-left', { taille: 16 })}${texte || ('Item ' + n)}</button>`;
}

// L'article d'un sujet LCA Pro, en une ligne : de quoi l'ouvrir sans quitter la question.
function ligneArticle(d) {
    var l = d && d.lca; if (!l) return '';
    var a = l.article || {}, revue = (a.revue || '') + (l.annee ? ' ' + l.annee : '');
    return html`<div class="qcm-article">
        ${ico('newspaper', { taille: 18 })}
        <span class="qcm-article-titre">${a.titre || l.titre || 'Article de lecture critique'}</span>
        ${revue.trim() ? html`<span class="qcm-article-revue">${revue.trim()}</span>` : ''}
        ${a.pdf ? html`<a class="btn btn-fantome btn-sm" href="${I().imgUrl(a.pdf)}" target="_blank" rel="noopener">${ico('file-pdf', { taille: 16 })}Ouvrir l'article</a>` : ''}
    </div>`;
}

// ================================================================= le registre (items et quiz transversaux)

var TRIS = [['num', 'Numéro'], ['weak', 'Maîtrise la plus faible'], ['coverage', 'Les moins couverts'], ['recent', 'Travaillés récemment'], ['nb', 'Nombre de questions'], ['spec', 'Matière']];

function segmentsVue(v) {
    var nbExtra = I().extraItems().length;
    var ONGLETS = [['list', 'Items', 'list-bullets', 0], ['quiz', 'Quiz transversaux', 'stack', nbExtra], ['stats', 'Statistiques', 'chart-pie', 0]];
    return html`<div class="segments qcm-segments" role="tablist" aria-label="Vues du QCM">${ONGLETS.map(function(o) {
        return html`<button type="button" role="tab" aria-selected="${v === o[0] ? 'true' : 'false'}" data-action="qcmView" data-args="${attr([o[0]])}">${ico(o[2], { taille: 16 })}${o[1]}${o[3] ? html` <span class="num">(${o[3]})</span>` : ''}</button>`;
    })}</div>`;
}

function enteteRegistre(v) {
    var idx = state.qcm.index || {}, nbItems = Object.keys(idx).length, nbQ = 0;
    Object.keys(idx).forEach(function(k) { nbQ += idx[k].nb || 0; });
    var log = state.learning.log, aujourdhui = 0;
    var debut = window.todayLogical ? window.todayLogical() : new Date();
    if (!window.todayLogical) debut.setHours(0, 0, 0, 0);
    log.forEach(function(e) { if (e.t >= debut.getTime()) aujourdhui++; });
    // Deux comptes cohabitent : les questions déposées sur le serveur et les 367 items du programme, qui sont
    // du référentiel local. Quand la banque manque, annoncer « 0 question sur 0 item » au-dessus d'une liste de
    // 367 lignes se contredisait : l'en-tête dit alors ce qui manque, et la barre d'outils ce qu'elle liste.
    var resume = state.qcm.indexLoading ? 'Chargement de la banque…'
        : state.qcm.indexError ? html`Aucun QCM déposé · ${pluriel(log.length, 'réponse enregistrée', 'réponses enregistrées')} · ${aujourdhui} aujourd'hui`
        : html`${pluriel(nbQ, 'question', 'questions')} sur ${pluriel(nbItems, 'item', 'items')} · ${pluriel(log.length, 'réponse enregistrée', 'réponses enregistrées')} · ${aujourdhui} aujourd'hui`;
    return html`<header class="qcm-entete">
        <div class="qcm-entete-texte">
            <h1>QCM et révision active</h1>
            <p class="qcm-resume num">${resume}</p>
        </div>
        <button type="button" class="btn btn-fantome btn-sm" data-action="livreOuvrirChoix" title="Réunir les QCM en un livre imprimable : par collège et par item, les questions puis leurs corrections">${ico('file-pdf', { taille: 16 })}Extraire en PDF</button>
    </header>
    ${segmentsVue(v)}`;
}

// La barre d'outils suit la vue : les quiz hors programme n'ont ni matière du catalogue, ni rang, ni tri par
// couverture — n'afficher que la recherche y évite trois commandes qui ne feraient rien.
function outilsRegistre(v) {
    var specs = window.allSpecialties || [], quiz = v === 'quiz';
    return html`<div class="qcm-outils${quiz ? ' qcm-outils--simple' : ''}" role="search" aria-label="Fouiller la banque de QCM">
        <div class="qcm-recherche">
            <label for="qcm-search" class="sr-only">${quiz ? 'Rechercher un quiz' : 'Rechercher un item'}</label>
            ${ico('magnifying-glass', { taille: 18 })}
            <input id="qcm-search" class="saisie" type="text" value="${state.qcm.search || ''}" placeholder="${quiz ? 'Numéro, mot du titre ou spécialité' : 'Numéro, mot du titre ou matière'}" autocomplete="off" spellcheck="false" enterkeyhint="search" data-input-action="qcmSearch" aria-describedby="qcm-compte">
        </div>
        ${quiz ? '' : html`<span class="select-enveloppe qcm-select"><label for="qcm-spec" class="sr-only">Matière</label>
            <select id="qcm-spec" class="select saisie" data-change-action="qcmSpec">
                <option value="all">Toutes les matières</option>
                ${specs.map(function(sp) { return html`<option value="${sp}"${state.qcm.spec === sp ? html` selected` : ''}>${sp}</option>`; })}
            </select></span>
        <span class="select-enveloppe qcm-select"><label for="qcm-tri" class="sr-only">Trier</label>
            <select id="qcm-tri" class="select saisie" data-change-action="qcmSort">
                ${TRIS.map(function(t) { return html`<option value="${t[0]}"${state.qcm.sort === t[0] ? html` selected` : ''}>${t[1]}</option>`; })}
            </select></span>
        <label class="qcm-coche"><input type="checkbox"${state.qcm.onlyWithQcm ? html` checked` : ''} data-change-action="qcmOnlyWithQcm"><span>Avec QCM seulement</span></label>`}
        <p id="qcm-compte" class="qcm-compte num" role="status" aria-live="polite" aria-atomic="true"></p>
    </div>`;
}

// Une ligne du registre : numéro, titre, aimants, maîtrise, trois actions. La même anatomie pour un item du
// catalogue et pour un quiz transversal — c'est le même geste qui les ouvre.
function ligneRegistre(o) {
    var i = I(), n = o.n, ix = o.ix, M = o.M, parts = o.parts;
    return html`<div class="qcm-ligne${ix ? '' : ' qcm-ligne--sans'}" role="listitem" data-item="${n}">
        <button type="button" class="qcm-ligne-ouvrir" data-action="qcmOpenItem" data-args="${attr([n, '', '', ''])}">
            <span class="qcm-ligne-num num">${n}</span>
            <span class="qcm-ligne-titre">${o.titre}</span>
        </button>
        <div class="qcm-ligne-meta">
            ${o.spec ? html`<span class="puce puce-sans-point ${window.getSpecialtyStyle(o.spec)}">${o.spec}</span>` : ''}
            ${ix ? html`<span class="puce puce-sans-point num">${pluriel(ix.nb || 0, 'question', 'questions')}</span>` : html`<span class="puce puce-sans-point" title="Aucun fichier de questions déposé sur le serveur pour cet item">Sans QCM</span>`}
            ${ix && ix.ped ? html`<span class="puce puce-pedia num">${ix.ped} en pédiatrie</span>` : ''}
            ${M ? html`<span class="puce puce-sans-point num" title="Questions distinctes déjà vues">${pluriel(M.qCount, 'question vue', 'questions vues')}</span>` : ''}
            ${parts ? html`<span class="puce puce-sans-point">${pluriel(parts.length, 'sous-partie', 'sous-parties')}</span>` : ''}
        </div>
        <div class="qcm-ligne-maitrise">${jaugeMaitrise(M ? M.mastery : null, { aide: M ? 'Maîtrise ' + pct(M.mastery) + ' % — ' + motMaitrise(M.mastery) + ' · ' + pluriel(M.n, 'réponse', 'réponses') + ', dernière le ' + i.fmtDate(M.last) : 'Jamais testé' })}</div>
        <div class="qcm-ligne-actions">
            ${ix ? html`<button type="button" class="btn btn-secondaire btn-sm" data-action="qcmStartSession" data-args="${attr([n, 'adaptive', '', 10])}" title="Session ciblée de 10 questions : les jamais vues d'abord, puis les ratées et les notions faibles">${ico('lightning', { taille: 16 })}<span class="qcm-action-texte">Tester</span></button>` : ''}
            <button type="button" class="btn btn-fantome btn-sm" data-action="flashOpen" data-args="${attr([n, '', false])}" title="Flashcards de cet item">${ico('cards-three', { taille: 16 })}<span class="qcm-action-texte">Cartes</span></button>
            <button type="button" class="btn btn-fantome btn-sm" data-action="recallOpen" data-args="${attr([n, ''])}" title="Active recall sur la fiche du podcast">${ico('brain', { taille: 16 })}<span class="qcm-action-texte">Recall</span></button>
        </div>
    </div>`;
}

function lignesRegistre() {
    var i = I(), v = state.qcm.view;
    if (v === 'quiz') {
        var idx = state.qcm.index || {}, Mall = window.computeMastery(), q = i.nrm(state.qcm.search || '');
        return i.extraItems().map(function(n) {
            var ix = idx[n];
            return { n: n, ix: ix, M: Mall[n], parts: null, titre: (ix && ix.titre) || ('Quiz ' + n), spec: (ix && ix.spec) || '' };
        }).filter(function(o) { return !q || o.n.indexOf(q) >= 0 || i.nrm(o.titre).indexOf(q) >= 0 || i.nrm(o.spec).indexOf(q) >= 0; });
    }
    return i.listRows().map(function(r) {
        return { n: r.n, ix: r.ix, M: r.M, parts: i.partsFor(r.n), titre: r.it.titre, spec: r.it.matiere };
    });
}

function texteCompte(nb) {
    var v = state.qcm.view;
    if (v === 'quiz') return nb ? pluriel(nb, 'quiz hors programme', 'quiz hors programme') : 'Aucun quiz hors programme';
    var t = nb ? pluriel(nb, 'item', 'items') : 'Aucun item';
    // Index absent : le compte porte son objet, sinon il contredit le « Aucun QCM déposé » de l'en-tête.
    if (state.qcm.indexError) t += ' du programme · aucun QCM déposé';
    if (state.qcm.spec !== 'all') t += ' · ' + state.qcm.spec;
    if (state.qcm.onlyWithQcm) t += ' · avec QCM';
    var q = (state.qcm.search || '').trim();
    if (q) t += ' pour « ' + q + ' »';
    return t;
}

// Le registre monte par tranches : les 367 items d'un coup font 62 000 px de page sur un téléphone, soit cent
// soixante écrans pour atteindre l'item 231. La recherche et les filtres, eux, portent toujours sur la totalité
// — c'est par eux qu'on atteint un item, pas par le pouce.
var TRANCHE = 60;
function cleFiltres() {
    return [state.qcm.view, state.qcm.search || '', state.qcm.spec, state.qcm.sort, state.qcm.onlyWithQcm ? 1 : 0].join('');
}
// Un pied de liste, pas une pagination : le compte en direct reste dans la barre d'outils, ici on dit
// seulement combien de lignes sont montées et comment en voir plus.
function piedRegistre(montrees, total) {
    var reste = total - montrees;
    if (reste <= 0) return total > TRANCHE ? html`<p class="qcm-pied-note num" role="status">Les ${total} items sont affichés.</p>` : '';
    var lot = Math.min(TRANCHE, reste);
    return html`<p class="qcm-pied-note num" role="status">${montrees} des ${total} items affichés.</p>
        <button type="button" class="btn btn-secondaire" data-action="qcmVoirPlus">${ico('caret-down', { taille: 16 })}${reste <= TRANCHE ? (reste > 1 ? 'Afficher les ' + reste + ' derniers items' : 'Afficher le dernier item') : 'Afficher ' + lot + ' items de plus'}</button>`;
}

// Le corps du registre : idempotent, appelé par afterRenderQcm et à chaque frappe. La frappe ne redessine que
// les lignes, le focus ne quitte jamais le champ.
export function renderQcmListe() {
    var b = document.getElementById('qcm-body'); if (!b) return;
    var compte = document.getElementById('qcm-compte'), pied = document.getElementById('qcm-pied');
    if (pied) pied.innerHTML = '';
    if (state.qcm.indexLoading && !state.qcm.index) {
        b.setAttribute('role', 'presentation');
        b.innerHTML = String(squeletteLignes());
        if (compte) compte.textContent = 'Chargement de l\'index…';
        return;
    }
    var lignes = lignesRegistre();
    if (compte) compte.textContent = texteCompte(lignes.length);
    if (!lignes.length) {
        b.setAttribute('role', 'presentation');
        b.innerHTML = String(html`<div class="vide">${ico('magnifying-glass')}<h2>${state.qcm.view === 'quiz' ? 'Aucun quiz hors programme' : 'Aucun item ne correspond'}</h2>
            <p>${state.qcm.view === 'quiz' ? 'Les banques hors programme (sémiologie, radiologie, anatomie pathologique…) apparaîtront ici dès qu\'elles seront déposées sur le serveur.' : 'Essaie un numéro, un mot du titre ou une autre matière.'}</p>
            ${state.qcm.view === 'quiz' ? '' : html`<button type="button" class="btn btn-fantome" data-action="qcmToutAfficher">Tout afficher</button>`}</div>`);
        return;
    }
    // Changer de recherche, de matière, de tri ou de vue remet la liste à sa première tranche.
    var cle = cleFiltres();
    if (state.qcm.visiblesCle !== cle) { state.qcm.visiblesCle = cle; state.qcm.visibles = TRANCHE; }
    var max = Math.max(TRANCHE, state.qcm.visibles || TRANCHE);
    var montrees = lignes.slice(0, max);
    b.setAttribute('role', 'list');
    window.renderRowsProgressive(b, montrees.map(function(o) { return String(ligneRegistre(o)); }), 30, 60);
    if (pied) pied.innerHTML = String(piedRegistre(montrees.length, lignes.length));
}

// « Afficher N items de plus » : la tranche suivante, puis le focus reste sur le bouton tant qu'il en reste ;
// à la dernière tranche, il passe à la première ligne montée pour ne pas retomber en haut de page.
window.actions.qcmVoirPlus = function() {
    var avant = Math.max(TRANCHE, state.qcm.visibles || TRANCHE);
    state.qcm.visibles = avant + TRANCHE;
    renderQcmListe();
    var bouton = document.querySelector('#qcm-pied [data-action="qcmVoirPlus"]');
    var cible = bouton || document.querySelectorAll('#qcm-body .qcm-ligne-ouvrir')[avant];
    if (cible) { try { cible.focus({ preventScroll: !!bouton }); } catch (e) {} }
};

function squeletteLignes() {
    return html`<div class="squelettes" aria-hidden="true">${[1, 2, 3, 4, 5, 6, 7].map(function() {
        return html`<div class="qcm-ligne qcm-ligne--squelette"><div class="qcm-ligne-ouvrir"><span class="squelette squelette-ligne" style="width:52%"></span></div><div class="qcm-ligne-meta"><span class="squelette squelette-ligne" style="width:28%;height:18px"></span></div></div>`;
    })}</div>`;
}

function vueRegistre() {
    var v = state.qcm.view;
    return html`${enteteRegistre(v)}
        ${noticeHtml()}
        ${state.qcm.indexError ? html`<div class="qcm-alerte" role="alert">${ico('warning-circle', { taille: 20 })}<div><p>${state.qcm.indexError}</p>
            <p class="qcm-alerte-aide">Sur le serveur, le dossier <code>edn_data/qcm/</code> (hors <code>public_html</code>) doit contenir les fichiers <code>NNN - Titre.json</code> du scraper et un sous-dossier <code>images/</code>.</p></div></div>` : ''}
        ${v === 'quiz' ? html`<p class="qcm-intro">Les banques d'edn.fr hors des 367 items — sémiologie, radiologie, anatomie pathologique, neurochirurgie — et les sujets de LCA Pro. Même fonctionnement : sessions ciblées, maîtrise par notion, flashcards et active recall construits sur les notions des questions, puisqu'il n'y a pas de fiche.</p>` : ''}
        ${outilsRegistre(v)}
        <div id="qcm-body" class="qcm-liste" role="list" aria-label="${v === 'quiz' ? 'Quiz hors programme' : 'Items du programme'}">${squeletteLignes()}</div>
        <div id="qcm-pied" class="qcm-pied"></div>`;
}

// ================================================================= statistiques

function ligneStat(opts) {
    return html`<button type="button" class="qcm-stat-ligne" data-action="qcmOpenItem" data-args="${attr(opts.args)}"${opts.titre ? html` title="${opts.titre}"` : ''}>
        <span class="qcm-stat-nom">${opts.avant || ''}<span class="qcm-stat-texte">${opts.texte}</span>${opts.apres ? html`<span class="qcm-stat-apres num">${opts.apres}</span>` : ''}</span>
        ${jaugeMaitrise(opts.mastery, { mot: false })}
    </button>`;
}

function vueStats() {
    var i = I(), Mall = window.computeMastery(), spec = state.qcm.statsSpec || 'all';
    var rows = Object.keys(Mall).map(function(n) { return { n: n, M: Mall[n], it: i.itemMeta(n), spec: i.specOf(n) }; })
        .filter(function(r) { return spec === 'all' || r.spec === spec || (r.it && r.it.collegesSecondaires.indexOf(spec) >= 0); });
    var testes = rows.filter(function(r) { return r.M.n >= 3; }).sort(function(a, b) { return a.M.mastery - b.M.mastery; });
    var peu = rows.filter(function(r) { return r.M.n < 3; });
    var notions = [];
    rows.forEach(function(r) { Object.keys(r.M.notions).forEach(function(k) { var nn = r.M.notions[k]; if (nn.n >= 2) notions.push({ n: r.n, nn: nn }); }); });
    notions.sort(function(a, b) { return a.nn.mastery - b.nn.mastery || b.nn.n - a.nn.n; });
    var faibles = notions.slice(0, 25), fortes = notions.slice().reverse().filter(function(x) { return x.nn.mastery >= 0.8; }).slice(0, 10);
    var parSpec = {};
    rows.forEach(function(r) { var k = r.spec || (r.it ? r.it.matiere : 'Quiz transversaux'); var e = parSpec[k] || (parSpec[k] = { w: 0, ws: 0, n: 0, items: 0 }); e.w += r.M.w; e.ws += r.M.ws; e.n += r.M.n; e.items++; });
    var specRows = Object.keys(parSpec).map(function(k) { return { spec: k, m: parSpec[k].ws / parSpec[k].w, n: parSpec[k].n, items: parSpec[k].items }; }).sort(function(a, b) { return a.m - b.m; });
    var idx = state.qcm.index || {};

    return html`${enteteRegistre('stats')}
        ${noticeHtml()}
        <div class="qcm-stats-outils">
            <span class="select-enveloppe qcm-select"><label for="qcm-stats-spec" class="sr-only">Matière</label>
                <select id="qcm-stats-spec" class="select saisie" data-change-action="qcmStatsSpec">
                    <option value="all">Toutes les matières</option>
                    ${(window.allSpecialties || []).map(function(sp) { return html`<option value="${sp}"${spec === sp ? html` selected` : ''}>${sp}</option>`; })}
                </select></span>
            <p class="qcm-stats-methode">La maîtrise est la moyenne de tes scores — QCM, flashcards, active recall — pondérée par leur fraîcheur (demi-vie de 45 jours) et par leur source : une question officielle pèse plus qu'une carte, une carte plus qu'une auto-évaluation. Un item apparaît après 3 réponses, une notion après 2.</p>
        </div>
        ${!rows.length ? html`<div class="vide">${ico('chart-pie')}<h2>Rien à mesurer pour l'instant</h2><p>Lance une session de QCM depuis le registre : la maîtrise par item et par notion se remplit toute seule.</p>
            <button type="button" class="btn btn-primaire" data-action="qcmView" data-args="${attr(['list'])}">Ouvrir le registre</button></div>`
        : html`<div class="qcm-stats">
            <section class="qcm-bloc">
                <h2>Items, du plus fragile au plus solide</h2>
                <p class="qcm-bloc-note num">${pluriel(testes.length, 'item mesuré', 'items mesurés')}</p>
                ${testes.length ? html`<div class="qcm-stat-liste">${testes.map(function(r) {
                    var couv = idx[r.n] && idx[r.n].nb ? Math.round(r.M.qCount / idx[r.n].nb * 100) : null;
                    var tendance = r.M.trend == null ? '' : (r.M.trend > 0.05 ? 'en progrès' : r.M.trend < -0.05 ? 'en recul' : 'stable');
                    return ligneStat({ args: [r.n, '', '', ''], texte: '#' + r.n + ' ' + i.itemTitle(r.n), mastery: r.M.mastery,
                        apres: pluriel(r.M.n, 'réponse', 'réponses') + (couv != null ? ' · ' + couv + ' % des QCM vus' : '') + (tendance ? ' · ' + tendance : '') });
                })}</div>` : html`<p class="qcm-bloc-vide">Aucun item n'a encore trois réponses.</p>`}
                ${peu.length ? html`<p class="qcm-bloc-note">${pluriel(peu.length, 'item commencé', 'items commencés')} avec moins de trois réponses : <span class="num">${peu.map(function(r) { return '#' + r.n; }).join(', ')}</span></p>` : ''}
            </section>
            <div class="qcm-stats-colonne">
                <section class="qcm-bloc">
                    <h2>Notions les plus fragiles</h2>
                    ${faibles.length ? html`<div class="qcm-stat-liste">${faibles.map(function(x) {
                        return ligneStat({ args: [x.n, '', encodeURIComponent(x.nn.key), ''], avant: puceRang(x.nn.rang), texte: x.nn.title,
                            apres: '#' + x.n + ' · ' + pluriel(x.nn.n, 'réponse', 'réponses'), mastery: x.nn.mastery });
                    })}</div>` : html`<p class="qcm-bloc-vide">Aucune notion n'a encore deux réponses.</p>`}
                </section>
                <section class="qcm-bloc">
                    <h2>Ce qui est acquis</h2>
                    ${fortes.length ? html`<div class="qcm-stat-liste">${fortes.map(function(x) {
                        return ligneStat({ args: [x.n, '', encodeURIComponent(x.nn.key), ''], avant: puceRang(x.nn.rang), texte: x.nn.title, apres: '#' + x.n, mastery: x.nn.mastery });
                    })}</div>` : html`<p class="qcm-bloc-vide">Pas encore de notion au-dessus de 80 %.</p>`}
                </section>
                <section class="qcm-bloc">
                    <h2>Par matière</h2>
                    ${specRows.length ? html`<div class="qcm-stat-liste">${specRows.map(function(r) {
                        return html`<div class="qcm-stat-ligne qcm-stat-ligne--inerte">
                            <span class="qcm-stat-nom"><span class="puce puce-sans-point ${window.getSpecialtyStyle(r.spec)}">${r.spec}</span><span class="qcm-stat-apres num">${pluriel(r.items, 'item', 'items')} · ${pluriel(r.n, 'réponse', 'réponses')}</span></span>
                            ${jaugeMaitrise(r.m, { mot: false })}</div>`;
                    })}</div>` : ''}
                </section>
            </div>
        </div>`}`;
}

// ================================================================= la fiche d'un item

// Une entrée du registre d'exercices : icône, titre, état en une phrase, actions. Quatre lignes du même
// registre au lieu de quatre cartes de quatre couleurs.
function exercice(opts) {
    return html`<div class="qcm-exo">
        <span class="qcm-exo-ico">${ico(opts.icone, { taille: 20 })}</span>
        <div class="qcm-exo-texte">
            <h3>${opts.titre}</h3>
            <p>${opts.phrase}</p>
        </div>
        <div class="qcm-exo-actions">${opts.actions}</div>
    </div>`;
}

function ongletsParties(n) {
    var i = I(), parts = i.partsFor(n); if (!parts) return '';
    var cur = state.qcm.part, carte = i.partMap(n, 'notions'), d = state.qcm.items[n], comptes = {};
    if (d && carte) d.questions.forEach(function(q) { var p = i.questionPart(q, carte); comptes[p] = (comptes[p] || 0) + 1; });
    var busy = state.qcm.busy === 'map';
    // Un groupe de choix qui passe à la ligne, jamais une bande qui défile : les noms de sous-parties sont longs
    // et nombreux (un item scindé en compte jusqu'à six). Chaque choix est entier, lisible et pointable à 44 px ;
    // le compte de questions est dans le libellé, donc visible sans ouvrir quoi que ce soit.
    // « Plusieurs sous-parties à la fois » : chaque sous-partie devient une case à cocher (toutes aria-pressed
    // indépendamment) ; « Item entier » reste le choix simple qui en sort.
    var coches = state.qcm.parts, nbCoches = coches ? coches.length : 0;
    var choix = [{ k: '', nom: 'Item entier', n: d ? d.questions.length : null, actif: coches ? !nbCoches : cur == null }]
        .concat(parts.map(function(p, k) { return { k: k, nom: p, n: carte ? (comptes[k] || 0) + (comptes[-1] || 0) : null, actif: coches ? coches.indexOf(k) >= 0 : cur === k }; }));
    return html`<div class="qcm-parties">
        <div class="qcm-choix" role="group" aria-label="${coches ? 'Sous-parties à réunir' : 'Partie de l\'item à travailler'}">
            ${choix.map(function(c) {
                var caseACocher = coches && c.k !== '';
                return html`<button type="button" class="qcm-choix-btn" aria-pressed="${c.actif ? 'true' : 'false'}" data-action="${caseACocher ? 'qcmCocherPartie' : 'qcmSetPart'}" data-args="${attr([c.k])}">${caseACocher ? ico(c.actif ? 'check-square' : 'square', { taille: 16 }) : ''}${c.nom}${c.n != null ? html`<span class="qcm-choix-n num">${c.n}</span>` : ''}</button>`;
            })}
        </div>
        <button type="button" class="btn btn-fantome btn-sm qcm-reunir" aria-pressed="${coches ? 'true' : 'false'}" data-action="qcmReunirParties" data-args="${attr([!coches])}" title="QCM et flashcards de plusieurs sous-parties dans la même séance, le temps de la séance">${ico('stack', { taille: 16 })}Plusieurs sous-parties à la fois</button>
        ${coches ? html`<p class="qcm-parties-note qcm-parties-reunion" role="status">${nbCoches >= 2
            ? pluriel(nbCoches, 'sous-partie réunie', 'sous-parties réunies') + ' : les QCM et les flashcards piochent dans toutes le temps de la séance. Chaque réponse et chaque carte restent rangées dans leur sous-partie.'
            : 'Coche au moins deux sous-parties : leurs QCM et leurs flashcards seront réunis le temps de la séance.'}</p>` : ''}
        ${busy ? html`<span class="puce puce-accent puce-attente">Répartition en cours</span>`
            : carte ? html`<span class="qcm-parties-note">Questions réparties par notion${comptes[-1] ? ', ' + pluriel(comptes[-1], 'transversale affichée partout', 'transversales affichées partout') : ''}.
                <button type="button" class="qcm-lien" data-action="qcmMapParts" data-args="${attr([String(n), 'notions'])}">Recalculer</button></span>`
            : boutonIA({ action: 'qcmMapParts', args: [String(n), 'notions'], label: 'Répartir les QCM par sous-partie', verrou: 'Répartition IA · abonnement', classe: 'btn-secondaire', titre: 'L\'IA rattache chaque notion des questions à sa sous-partie ; le résultat est mis en cache' })}
        ${!carte && (cur != null || nbCoches >= 2) ? html`<p class="qcm-parties-avert">${ico('info', { taille: 16 })}Tant que la répartition n'est pas lancée, ${nbCoches >= 2 ? 'des sous-parties réunies affichent' : 'une sous-partie affiche'} toutes les questions de l'item.</p>` : ''}
    </div>`;
}

function bascPedia(n) {
    var i = I(), meta = i.itemMeta(n); if (!meta) return '';
    var d = state.qcm.items[n];
    var aPedia = state.qcm.pedia || meta.matiere === 'Pédiatrie' || (meta.collegesSecondaires || []).indexOf('Pédiatrie') >= 0 || (d && d.questions.some(function(q) { return q.est_pediatrique; }));
    if (!aPedia || meta.matiere === 'Pédiatrie') return '';
    return html`<div class="qcm-angle">
        <span class="qcm-angle-label" id="qcm-angle-label">Angle</span>
        <div class="segments" role="tablist" aria-labelledby="qcm-angle-label">
            <button type="button" role="tab" aria-selected="${state.qcm.pedia ? 'false' : 'true'}" data-action="qcmOpenItem" data-args="${attr([String(n), porteeArg(), '', ''])}">Item complet</button>
            <button type="button" role="tab" aria-selected="${state.qcm.pedia ? 'true' : 'false'}" data-action="qcmOpenItem" data-args="${attr([String(n), porteeArg(), '', 'true'])}" title="Questions pédiatriques, flashcards et mini-cours sur les particularités de l'enfant">Renforcement pédiatrie</button>
        </div>
    </div>`;
}

function ligneQuestion(q, n, M) {
    var i = I(), dernier = M && M.qLast[q.id];
    var texte = q.enonce || i.stripHtml(q.enonce_html);
    return html`<button type="button" class="qcm-qligne" data-action="qcmStartSession" data-args="${attr([String(n), 'single', porteeArg(), q.id])}">
        <span class="qcm-qligne-score num ${dernier === undefined ? 'qcm-qligne-neuve' : ''}">${dernier === undefined ? 'jamais vue' : pct(dernier) + ' %'}</span>
        <span class="qcm-qligne-corps">
            <span class="qcm-qligne-texte">${texte.slice(0, 260)}</span>
            <span class="qcm-qligne-meta">
                <span class="puce puce-sans-point">${i.typeLabel(q)}</span>
                ${q.est_pediatrique ? html`<span class="puce puce-pedia">Pédiatrie</span>` : ''}
                ${q._ai ? html`<span class="puce puce-sans-point">Écrite par l'IA</span>` : ''}
                ${(q.images || []).length || q.fichier ? html`<span class="puce puce-sans-point">Image</span>` : ''}
                ${(q.notions || []).slice(0, 3).map(function(x) { return html`<span class="qcm-qligne-notion">${x.intitule}</span>`; })}
            </span>
        </span>
        ${ico('caret-right', { taille: 16, classe: 'qcm-qligne-chevron' })}
    </button>`;
}

function vueItem() {
    var i = I(), n = state.qcm.current, d = state.qcm.items[n], meta = i.itemMeta(n), M = i.itemMastery(n);
    // reunies : plusieurs sous-parties cochées — QCM et flashcards portent sur toutes, pa (active recall,
    // mini-cours) reste la sous-partie simple ; pq est la portée passée aux QCM et aux flashcards.
    var part = state.qcm.part, parts = i.partsFor(n), pa = partArg(), reunies = reunion(), pq = porteeArg();
    var portee = nomPortee(n, part, reunies);
    var ix = state.qcm.index && state.qcm.index[n];
    var qsPortee = d ? i.questionsForPart(d, n, reunies || part) : [];
    var ratees = d ? qsPortee.filter(function(q) { return M && M.qLast[q.id] !== undefined && M.qLast[q.id] < 1; }).length : 0;
    var jamais = d ? qsPortee.filter(function(q) { return !M || M.qLast[q.id] === undefined; }).length : 0;
    var nbIA = d ? qsPortee.filter(function(q) { return q._ai; }).length : 0;
    var deck = reunies ? i.deckReuni(n, reunies) : i.getDeck(n, part), dus = deck ? i.dueCards(deck).length : 0;
    var neuf = i.itemNeverSeen(n);
    var genBusy = state.qcm.busy === 'qcmgen';
    var lacunes = i.itemGaps(n, reunies || part), coursGarde = state.learning.cours[i.deckKey(n, part)];
    var faiblesCours = i.weakestNotions(n, 6).filter(function(x) { return x.mastery < 0.6; });

    var actionsQcm;
    if (!d && state.qcm.loadError[n]) actionsQcm = '';
    else if (!d) actionsQcm = html`<span class="qcm-exo-attente">${ico('circle-notch', { taille: 16, classe: 'qcm-tourne' })}Chargement…</span>`;
    else if (!qsPortee.length) actionsQcm = state.qcm.pedia ? boutonIA({ action: 'qcmGenPedia', args: [String(n)], label: 'Créer des QCM pédiatrie', verrou: 'QCM pédiatrie · abonnement', occupe: genBusy, enCours: 'Rédaction des QCM' }) : '';
    else actionsQcm = html`
        <button type="button" class="btn btn-primaire btn-sm" data-action="qcmStartSession" data-args="${attr([String(n), 'adaptive', pq, 10])}">${ico('lightning', { taille: 16 })}Session ciblée (10)</button>
        <button type="button" class="btn btn-fantome btn-sm" data-action="qcmStartSession" data-args="${attr([String(n), 'adaptive', pq, 20])}" title="Même sélection, vingt questions">Session de 20</button>
        <button type="button" class="btn btn-fantome btn-sm" data-action="qcmStartSession" data-args="${attr([String(n), 'all', pq, ''])}">Toutes (${qsPortee.length})</button>
        ${ratees ? html`<button type="button" class="btn btn-fantome btn-sm" data-action="qcmStartSession" data-args="${attr([String(n), 'errors', pq, ''])}">${ico('arrow-counter-clockwise', { taille: 16 })}Mes erreurs (${ratees})</button>` : ''}
        ${state.qcm.pedia ? boutonIA({ action: 'qcmGenPedia', args: [String(n)], label: 'Créer des QCM pédiatrie', verrou: 'QCM pédiatrie · abonnement', classe: 'btn-secondaire', occupe: genBusy, enCours: 'Rédaction des QCM' }) : ''}`;

    return html`<div class="qcm-item">
        <header class="qcm-entete qcm-entete--item">
            <div class="qcm-entete-texte">
                ${html`<button type="button" class="qcm-retour" data-action="qcmBackToList">${ico('arrow-left', { taille: 16 })}${meta ? 'Tous les items' : 'Quiz transversaux'}</button>`}
                <h1><span class="num">#${n}</span> ${i.itemTitle(n)}</h1>
                <div class="qcm-item-puces">
                    ${i.specOf(n) ? html`<span class="puce puce-sans-point ${window.getSpecialtyStyle(i.specOf(n))}">${i.specOf(n)}</span>` : ''}
                    ${state.qcm.pedia ? html`<span class="puce puce-pedia">Renforcement pédiatrie</span>` : ''}
                    ${portee ? html`<span class="puce puce-accent">${portee}</span>` : ''}
                    ${!meta ? html`<span class="puce puce-sans-point">Quiz hors programme</span>` : ''}
                    ${d ? html`<span class="puce puce-sans-point num">${pluriel(d.questions.length, 'question', 'questions')} · ${pluriel(d._notions.length, 'notion', 'notions')}</span>` : ''}
                    ${d && (d._hidden || []).length ? html`<button type="button" class="qcm-lien" data-action="qcmUnhide" data-args="${attr([String(n)])}">${pluriel(d._hidden.length, 'question masquée', 'questions masquées')} — rétablir</button>` : ''}
                    ${ix && ix.extras ? html`<span class="puce puce-sans-point">inclut les QCM de l'item ${ix.extras.map(function(e) { return e.n; }).join(', ')}</span>` : ''}
                </div>
            </div>
            <div class="qcm-item-maitrise">
                <p class="qcm-item-maitrise-label">Maîtrise${portee ? ' de l\'item entier' : ''}</p>
                ${jaugeMaitrise(M ? M.mastery : null, { large: true })}
                <p class="qcm-item-maitrise-note num">${M ? html`${pluriel(M.n, 'réponse', 'réponses')} · dernière le ${i.fmtDate(M.last)}${M.trend != null ? (M.trend > 0.05 ? ' · en progrès' : M.trend < -0.05 ? ' · en recul' : ' · stable') : ''}` : 'Aucune réponse enregistrée sur cet item'}</p>
            </div>
        </header>
        ${ongletsParties(n)}
        ${bascPedia(n)}
        ${noticeHtml()}
        ${ligneArticle(d)}
        ${state.qcm.loadError[n] && !d ? html`<div class="qcm-alerte" role="alert">${ico('warning-circle', { taille: 20 })}<div><p>${state.qcm.loadError[n]}</p><p class="qcm-alerte-aide">Les flashcards et l'active recall restent disponibles.</p></div></div>` : ''}

        <section class="qcm-exos" aria-label="Exercices de cet item">
            ${exercice({ icone: 'question', titre: 'QCM' + (state.qcm.pedia ? ' pédiatriques' : '') + (portee ? ' · ' + portee : ''),
                phrase: !d ? (state.qcm.loadError[n] ? 'Pas de banque de questions déposée pour cet item.' : 'Chargement des questions…')
                    : (!qsPortee.length ? (state.qcm.pedia ? 'Aucune question pédiatrique dans la banque pour cet item : l\'IA peut en rédiger à partir de la fiche et du collège de pédiatrie.' : 'Aucune question dans ce périmètre.')
                        : pluriel(qsPortee.length, 'question', 'questions') + (nbIA ? ' (dont ' + nbIA + ' écrites par l\'IA)' : '') + ' · ' + jamais + ' jamais vues · ' + pluriel(ratees, 'ratée', 'ratées')),
                actions: actionsQcm })}
            ${exercice({ icone: 'cards-three', titre: 'Flashcards' + (reunies ? ' · ' + portee : ''),
                phrase: reunies
                    ? (deck ? pluriel(deck.cards.length, 'carte', 'cartes') + ' dans les paquets de ces sous-parties · ' + (dus ? pluriel(dus, 'à revoir maintenant', 'à revoir maintenant') : 'paquets à jour')
                        : 'Aucune carte dans ces sous-parties pour l\'instant : les cartes se créent sous-partie par sous-partie.')
                    : deck && deck.cards.length ? pluriel(deck.cards.length, 'carte', 'cartes') + ' · ' + (dus ? pluriel(dus, 'à revoir maintenant', 'à revoir maintenant') : 'paquet à jour')
                    : (state.qcm.pedia ? 'Paquet dédié à la pédiatrie : des cartes sur les particularités de l\'enfant seulement.'
                        : neuf ? 'Item jamais travaillé : des cartes de découverte, construites sur le plan de ta fiche.'
                        : 'Des cartes ciblées sur les points faibles repérés par tes QCM.'),
                actions: html`<button type="button" class="btn btn-secondaire btn-sm" data-action="flashOpen" data-args="${attr([String(n), pq, false])}">${ico('cards-three', { taille: 16 })}${dus ? 'Réviser ' + pluriel(dus, 'carte', 'cartes') : 'Ouvrir les flashcards'}</button>` })}
            ${reunies ? html`<p class="qcm-exos-note">${ico('info', { taille: 16 })}L'active recall et le mini-cours se font une sous-partie à la fois : coche-en une seule pour les retrouver.</p>` : html`
            ${exercice({ icone: 'brain', titre: 'Active recall',
                phrase: 'Section par section de la fiche à suivre du podcast : une consigne, tu récites de mémoire, tu compares, l\'IA évalue.',
                actions: html`<button type="button" class="btn btn-secondaire btn-sm" data-action="recallOpen" data-args="${attr([String(n), pa])}">${ico('brain', { taille: 16 })}Lancer l'active recall</button>` })}
            ${exercice({ icone: 'graduation-cap', titre: 'Mini-cours ciblé',
                phrase: coursGarde ? 'Dernier mini-cours écrit le ' + i.fmtDate(coursGarde.updated) + '. Il repart de tes lacunes et de tes notions les plus faibles.'
                    : (lacunes.length || faiblesCours.length ? 'L\'IA explique tes points faibles en s\'appuyant sur la fiche EDN et les collèges.' : 'Réponds à des QCM ou fais un active recall analysé : le mini-cours part de ce qui a manqué.'),
                actions: html`<button type="button" class="btn btn-fantome btn-sm" data-action="coursOpen" data-args="${attr([String(n), pa, ''])}">${ico('graduation-cap', { taille: 16 })}${coursGarde ? 'Relire le mini-cours' : 'Ouvrir le mini-cours'}</button>` })}`}
        </section>

        ${lacunes.length ? html`<section class="qcm-bloc">
            <h2>Points à retravailler</h2>
            <p class="qcm-bloc-note">${(function() { var err = lacunes.filter(function(g) { return g.type === 'erreur'; }).length;
                return err ? pluriel(err, 'erreur', 'erreurs') + ' et ' + pluriel(lacunes.length - err, 'lacune repérée', 'lacunes repérées') + ' en active recall' : pluriel(lacunes.length, 'lacune repérée', 'lacunes repérées') + ' en active recall'; })()}</p>
            <ul class="qcm-lacunes">${lacunes.slice(0, 8).map(function(g) {
                return html`<li class="qcm-lacune${g.type === 'erreur' ? ' qcm-lacune--erreur' : ''}">
                    <span class="puce ${g.type === 'erreur' ? 'puce-danger' : 'puce-warn'}">${g.type === 'erreur' ? 'Erreur' : 'Oubli'}</span>
                    <span class="qcm-lacune-texte"><strong>${g.titre}</strong>${g.detail ? html` — ${g.detail}` : ''}</span>
                    <span class="num qcm-lacune-date">${i.fmtDate(g.t)}</span>
                </li>`;
            })}</ul>
        </section>` : ''}

        ${blocNotions(n, d, M)}
        ${d ? blocQuestions(n, d, M, qsPortee) : ''}
    </div>`;
}

function blocNotions(n, d, M) {
    var i = I();
    if (!d && !M) return '';
    var part = state.qcm.part, reunies = reunion();
    var ns = i.notionStats(n, { part: part, pedia: state.qcm.pedia });
    // sous-parties réunies : les notions de chacune, maîtrise lue sur l'item entier
    if ((part != null || reunies) && d) {
        var carte = i.partMap(n, 'notions');
        if (carte) ns = ns.filter(function(x) { var p = carte[x.key]; return p === undefined || p < 0 || (reunies ? reunies.indexOf(p) >= 0 : p === part); });
    }
    var testees = ns.filter(function(x) { return x.n > 0; }).sort(function(a, b) { return (a.mastery - b.mastery) || (b.n - a.n); });
    var jamais = ns.filter(function(x) { return !x.n; });
    return html`<section class="qcm-bloc">
        <h2>Notions, de la plus fragile à la plus sûre</h2>
        <p class="qcm-bloc-note num">${pluriel(testees.length, 'testée', 'testées')} · ${pluriel(jamais.length, 'jamais testée', 'jamais testées')}</p>
        ${testees.length ? html`<div class="qcm-notions">${testees.map(function(x) {
            var src = Object.keys(x.byMode || {}).map(function(k) { return ({ qcm: 'QCM', flash: 'cartes', recall: 'recall', recall_ai: 'recall relu par l\'IA' }[k] || k) + ' ' + x.byMode[k]; }).join(' · ');
            return html`<button type="button" class="qcm-notion" data-action="qcmFilterNotion" data-args="${attr([encodeURIComponent(x.key)])}" title="${(x.chap || '') + (src ? ' · ' + src : '')}">
                <span class="qcm-notion-nom">${puceRang(x.rang)}<span class="qcm-notion-titre">${x.title}</span><span class="num qcm-notion-nb">${pluriel(x.n, 'réponse', 'réponses')}${x.extra ? ' · hors banque' : ''}</span></span>
                ${jaugeMaitrise(x.mastery, { mot: false })}
            </button>`;
        })}</div>` : html`<p class="qcm-bloc-vide">Réponds à quelques questions : tes points faibles et tes points forts apparaîtront ici, notion par notion.</p>`}
        ${jamais.length ? html`<details class="qcm-jamais"><summary>${ico('caret-right', { taille: 16 })}Notions jamais testées (${jamais.length})</summary>
            <div class="qcm-jamais-liste">${jamais.map(function(x) {
                return html`<button type="button" class="qcm-notion-puce" data-action="qcmFilterNotion" data-args="${attr([encodeURIComponent(x.key)])}">${puceRang(x.rang)}<span>${x.title}</span><span class="num">×${x.count}</span></button>`;
            })}</div></details>` : ''}
    </section>`;
}

function blocQuestions(n, d, M, qsPortee) {
    var i = I(), liste = qsPortee, fn = state.qcm.filterNotion;
    if (fn) liste = liste.filter(function(q) { return q._nk.indexOf(fn) >= 0; });
    if (state.qcm.filterMode === 'errors') liste = liste.filter(function(q) { return M && M.qLast[q.id] !== undefined && M.qLast[q.id] < 1; });
    if (state.qcm.filterMode === 'unseen') liste = liste.filter(function(q) { return !M || M.qLast[q.id] === undefined; });
    var titreNotion = fn ? ((d._notions.filter(function(x) { return x.key === fn; })[0] || { title: fn }).title) : null;
    var MODES = [['all', 'Toutes'], ['unseen', 'Jamais vues'], ['errors', 'Ratées']];
    return html`<section class="qcm-bloc qcm-bloc--questions">
        <div class="qcm-questions-tete">
            <h2><button type="button" class="qcm-deplier" data-action="qcmToggleQuestions" aria-expanded="${state.qcm.showAllQuestions ? 'true' : 'false'}" aria-controls="qcm-questions">${ico('caret-right', { taille: 16, classe: 'qcm-caret' })}Questions (${liste.length})</button></h2>
            ${titreNotion ? html`<span class="puce puce-accent qcm-filtre">${titreNotion}<button type="button" class="qcm-filtre-x" data-action="qcmFilterNotion" data-args="${attr([''])}" aria-label="Retirer le filtre de notion">${ico('x', { taille: 14 })}</button></span>` : ''}
            ${fn ? html`<button type="button" class="btn btn-secondaire btn-sm" data-action="qcmStartSession" data-args="${attr([String(n), 'notion', porteeArg(), encodeURIComponent(fn)])}">${ico('lightning', { taille: 16 })}S'entraîner sur cette notion</button>` : ''}
            <div class="segments qcm-questions-modes" role="tablist" aria-label="Filtrer les questions">
                ${MODES.map(function(m) { return html`<button type="button" role="tab" aria-selected="${state.qcm.filterMode === m[0] ? 'true' : 'false'}" data-action="qcmFilterMode" data-args="${attr([m[0]])}">${m[1]}</button>`; })}
            </div>
        </div>
        <div id="qcm-questions" class="qcm-qlignes"${state.qcm.showAllQuestions ? '' : html` hidden`}>
            ${liste.length ? liste.map(function(q) { return ligneQuestion(q, n, M); }) : html`<p class="qcm-bloc-vide">Aucune question dans cette sélection.</p>`}
        </div>
    </section>`;
}

// ================================================================= la séance de QCM

var MODES_SEANCE = { adaptive: 'Session ciblée', all: 'Toutes les questions', errors: 'Mes erreurs', notion: 'Par notion', single: 'Une question', retry: 'Reprise des erreurs' };

function texteCompteur(s) {
    return 'Question ' + Math.min(s.idx + 1, s.list.length) + ' sur ' + s.list.length + (s.results.length ? ' · ' + pct(s.results.reduce(function(a, r) { return a + r.score; }, 0) / s.results.length) + ' % en moyenne' : '');
}

function enteteSeance(s) {
    if (s.annale) return enteteSeanceAnnale(s);
    var i = I(), d = state.qcm.items[s.n], portee = nomPortee(s.n, s.part, s.parts);
    return html`<header id="qcm-seance-tete" class="qcm-seance-tete">
        <div class="qcm-seance-qui">
            <p class="qcm-seance-mode">${MODES_SEANCE[s.mode] || 'Séance'} · <span class="num">item ${s.n}</span>${state.qcm.pedia ? ' · pédiatrie' : ''}${portee ? ' · ' + portee : ''}</p>
            <h1>${d ? (d.item_titre || i.itemTitle(s.n)) : i.itemTitle(s.n)}</h1>
        </div>
        <div class="qcm-seance-etat">
            ${avancement(s.results.length, s.list.length, { label: 'Avancement de la séance', texte: texteCompteur(s) })}
        </div>
        <button type="button" class="ico-btn" data-action="qcmQuit" aria-label="Quitter la séance" title="Quitter la séance — les réponses déjà validées restent comptées">${ico('x')}</button>
    </header>`;
}

function resumeSeance(s) {
    var i = I(), moyenne = s.results.length ? s.results.reduce(function(a, r) { return a + r.score; }, 0) / s.results.length : 0;
    var parNotion = {};
    s.results.forEach(function(r) { (r.q.notions || []).forEach(function(x) { var k = i.nrm(x.intitule); var e = parNotion[k] || (parNotion[k] = { title: x.intitule, rang: x.rang, n: 0, sum: 0 }); e.n++; e.sum += r.score; }); });
    var rangs = Object.keys(parNotion).map(function(k) { return parNotion[k]; }).sort(function(a, b) { return (a.sum / a.n) - (b.sum / b.n); });
    var fautes = s.results.filter(function(r) { return r.score < 1; }).length;
    var ps = s.parts || (s.part == null ? '' : s.part);   // mêmes sous-parties, réunies ou non
    return html`<div class="qcm-fin">
        <div class="qcm-fin-score">
            <p class="qcm-fin-chiffre num">${pct(moyenne)} %</p>
            <p class="qcm-fin-mot">${motMaitrise(moyenne)}</p>
            <p class="qcm-fin-note num">${pluriel(s.results.length, 'question', 'questions')} · ${fautes ? pluriel(fautes, 'à revoir', 'à revoir') : 'rien à revoir'}</p>
        </div>
        <div class="qcm-fin-notions">
            <h2>Notions travaillées</h2>
            ${rangs.length ? html`<div class="qcm-notions">${rangs.map(function(r) {
                return html`<div class="qcm-notion qcm-notion--inerte"><span class="qcm-notion-nom">${puceRang(r.rang)}<span class="qcm-notion-titre">${r.title}</span><span class="num qcm-notion-nb">×${r.n}</span></span>${jaugeMaitrise(r.sum / r.n, { mot: false })}</div>`;
            })}</div>` : html`<p class="qcm-bloc-vide">La banque ne rattache aucune notion à ces questions.</p>`}
        </div>
        <!-- Deux portes, et elles seules sur la première ligne : refaire ses erreurs, ou passer aux flashcards
             ciblées. Reprendre une autre session et sortir sont des suites, pas des portes : seconde ligne. -->
        ${s.annale ? finAnnale(s, fautes) : html`<div class="qcm-fin-suite">
            ${fautes ? html`<button type="button" class="btn btn-primaire" data-action="qcmRestartErrors">${ico('arrow-counter-clockwise', { taille: 18 })}Refaire ${pluriel(fautes, 'erreur', 'erreurs')}</button>` : ''}
            <button type="button" class="btn ${fautes ? 'btn-secondaire' : 'btn-primaire'}" data-action="flashOpen" data-args="${attr([String(s.n), ps, false])}">${ico('cards-three', { taille: 18 })}Flashcards sur mes points faibles</button>
        </div>
        <div class="qcm-fin-sortie">
            <button type="button" class="btn btn-fantome btn-sm" data-action="qcmStartSession" data-args="${attr([String(s.n), 'adaptive', ps, 10])}">${ico('lightning', { taille: 16 })}Nouvelle session ciblée</button>
            <button type="button" class="btn btn-fantome btn-sm" data-action="qcmQuit">${ico('arrow-left', { taille: 16 })}Retour à l'item</button>
        </div>`}
    </div>`;
}

// Bandeau du bilan du soir : il dit de quel chapitre on est en train de parler, où l'on en est dans la file,
// et le compte général depuis le début. Il porte aussi le passage à l'item suivant, ce qui permet d'abandonner
// un item en cours de route sans quitter le bilan.
function banniereSoir() {
    var b = state.qcm.soir; if (!b) return '';
    var a = window.avancementSoir(b); if (!a || !a.courant) return '';
    var e = a.courant, i = I();
    var nom = i.itemTitle(e.n) || e.titre;
    var dernier = a.restants === 0;
    return html`<aside class="qcm-soir" aria-label="Bilan du soir en cours">
        <div class="qcm-soir-qui">
            <p class="qcm-soir-mode">${ico('moon', { taille: 14 })}Bilan du soir · ${b.mode === 'flash' ? 'flashcards' : 'QCM'}<span class="num"> · ${a.rang} sur ${a.total}</span></p>
            <p class="qcm-soir-item"><span class="num">Item ${e.n}</span> ${nom}${e.partNom ? html` <span class="qcm-soir-partie">[${e.partNom}]</span>` : ''}${e.pedia ? ' · pédiatrie' : ''}</p>
        </div>
        <p class="qcm-soir-compte num" role="status">${b.mode === 'flash'
            ? pluriel(a.faits, 'carte revue', 'cartes revues')
            : a.faits + ' / ' + pluriel(a.justes, 'juste', 'justes')}${a.restants ? ' · ' + pluriel(a.restants, 'item restant', 'items restants') : ' · dernier item'}</p>
        <div class="qcm-soir-actions">
            <button type="button" class="btn btn-primaire btn-sm" data-action="soirSuivant">${dernier ? 'Terminer le bilan' : 'Item suivant'}</button>
            <button type="button" class="btn btn-fantome btn-sm" data-action="soirQuitter">Arrêter</button>
        </div>
    </aside>`;
}

// Fin du bilan : ce qui a été repris, ce qui a été sauté, et pourquoi.
function vueSoirFin() {
    var b = state.qcm.soir; if (!b) return vueRegistre();
    var i = I(), minutes = Math.round((Date.now() - (b.debut || Date.now())) / 60000);
    var repris = b.file.length - b.sautes.length;
    return html`<div class="qcm-flash">
        <header class="qcm-entete qcm-entete--exo"><div class="qcm-entete-texte">
            <h1>${ico('moon', { taille: 24 })}Bilan du soir terminé</h1>
            <div class="qcm-item-puces"><span class="puce puce-sans-point num">${pluriel(repris, 'item repris', 'items repris')}${minutes ? ' · ' + window.formatDuration(minutes) : ''}</span></div>
        </div></header>
        <div class="qcm-fin">
            <div class="qcm-fin-score">
                <p class="qcm-fin-chiffre num">${b.mode === 'flash' ? b.faits : (b.faits ? pct(b.justes / b.faits) + ' %' : '—')}</p>
                <p class="qcm-fin-mot">${b.mode === 'flash' ? (b.faits > 1 ? 'cartes revues' : 'carte revue') : 'de réussite'}</p>
                <p class="qcm-fin-note num">${b.mode === 'flash' ? pluriel(b.faits, 'carte', 'cartes') : b.justes + ' bonnes réponses sur ' + b.faits}</p>
            </div>
            <div class="qcm-fin-points">${b.file.map(function(e, k) {
                var saute = b.sautes.find(function(x) { return x.n === e.n && x.part === e.part; });
                return html`<div class="qcm-fin-point">
                    <div class="qcm-fin-point-tete">
                        <span class="qcm-fin-point-nom"><span class="num">${e.n}</span> ${i.itemTitle(e.n) || e.titre}${e.partNom ? ' [' + e.partNom + ']' : ''}</span>
                        <span class="qcm-fin-point-scores">${saute
                            ? html`<span class="puce puce-sans-point" title="Rien à reprendre sur cet item">passé · ${saute.raison}</span>`
                            : html`<span class="puce puce-ok">repris</span>`}</span>
                    </div></div>`;
            })}</div>
        </div>
        <div class="qcm-fin-suite">
            <button type="button" class="btn btn-primaire" data-action="soirQuitter">${ico('check', { taille: 18 })}C'est fini pour aujourd'hui</button>
        </div>
    </div>`;
}

function vueSeance() {
    var s = state.qcm.session; if (!s) return vueRegistre();
    if (s.finished) return html`<div class="qcm-seance">${banniereSoir()}${enteteSeance(s)}${resumeSeance(s)}</div>`;
    var q = I().curQ();
    if (!q) return html`<div class="qcm-seance">${enteteSeance(s)}<div class="vide">${ico('question')}<h2>Séance terminée</h2><button type="button" class="btn btn-secondaire" data-action="qcmQuit">Retour à l'item</button></div></div>`;
    return html`<div class="qcm-seance">
        ${banniereSoir()}
        ${enteteSeance(s)}
        ${noticeHtml()}
        ${s.annale ? dossierAnnale(q) : ligneArticle(state.qcm.items[s.n])}
        ${carteQuestion(q, s)}
    </div>`;
}

// ================================================================= flashcards

function vueFlash() {
    var i = I(), f = state.qcm.flash; if (!f) return vueRegistre();
    // révision réunie (f.parts) : les paquets des sous-parties vus comme un seul ; rien à y générer ni à supprimer
    var n = f.n, portee = nomPortee(n, f.part, f.parts), deck = f.parts ? i.deckReuni(n, f.parts) : i.getDeck(n, f.part);
    var fp = f.parts || (f.part == null ? '' : f.part);
    var busy = state.qcm.busy === 'flash';
    var frais = (deck && deck.lastWeakAt) ? i.newWeakSince(n, f.part, deck.lastWeakAt) : null;
    var rienDeNeuf = f.mode === 'weak' && frais && frais.total === 0;
    var pdf = window.LOCAL_FICHES && window.LOCAL_FICHES[n];
    var carte = deck && deck.cards.length ? f.queue[f.idx] : null;

    return html`<div class="qcm-flash">
        ${banniereSoir()}
        <header class="qcm-entete qcm-entete--exo">
            <div class="qcm-entete-texte">
                ${retourItem(n)}
                <h1>${ico('cards-three', { taille: 24 })}Flashcards · ${i.itemTitle(n)}</h1>
                <div class="qcm-item-puces">
                    ${portee ? html`<span class="puce puce-accent">${portee}</span>` : ''}
                    ${state.qcm.pedia ? html`<span class="puce puce-pedia">Renforcement pédiatrie</span>` : ''}
                    ${deck ? html`<span class="puce puce-sans-point num">${pluriel(deck.cards.length, 'carte', 'cartes')} · ${pluriel(i.dueCards(deck).length, 'à revoir', 'à revoir')} · ${deck.cards.filter(function(c) { return (c.reps || 0) > 0; }).length} déjà révisées</span>` : ''}
                </div>
            </div>
            <!-- Le pied de la carte porte déjà « Supprimer cette carte ». Ici l'objet n'est pas le même : c'est
                 le paquet entier. Un pictogramme rouge sans un mot laissait croire à un doublon. -->
            ${deck && !f.parts ? html`<button type="button" class="btn btn-fantome btn-sm qcm-vider" data-action="flashDeleteDeck" title="Le paquet entier, ses ${pluriel(deck.cards.length, 'carte', 'cartes')} et leurs échéances">${ico('trash-simple', { taille: 16 })}Supprimer le paquet</button>` : ''}
        </header>
        ${noticeHtml()}

        ${f.parts ? html`<p class="qcm-gen-source qcm-flash-reunion">${ico('stack', { taille: 16 })}Révision réunie : les cartes à revoir de ${pluriel(f.parts.length, 'sous-partie', 'sous-parties')}, chacune notée dans son propre paquet. Pour créer des cartes, ouvre une seule sous-partie.</p>` : html`
        <section class="qcm-gen" aria-label="Fabriquer des cartes">
            <div class="qcm-gen-ligne">
                <span class="qcm-gen-label" id="qcm-gen-mode">${deck ? 'Ajouter des cartes' : 'Créer le paquet'}</span>
                <div class="segments" role="tablist" aria-labelledby="qcm-gen-mode">
                    <button type="button" role="tab" aria-selected="${f.mode === 'discovery' ? 'true' : 'false'}" data-action="flashSetMode" data-args="${attr(['discovery'])}" title="Cartes de première découverte, construites sur le plan de ta fiche EDN">${ico('book-open', { taille: 16 })}Découverte</button>
                    <button type="button" role="tab" aria-selected="${f.mode === 'weak' ? 'true' : 'false'}" data-action="flashSetMode" data-args="${attr(['weak'])}" title="Cartes ciblées sur les notions les moins maîtrisées et les QCM ratés">${ico('crosshair', { taille: 16 })}Points faibles</button>
                </div>
                <span class="select-enveloppe qcm-select qcm-gen-nb"><label for="qcm-gen-count" class="sr-only">Nombre de cartes</label>
                    <select id="qcm-gen-count" class="select saisie" data-change-action="flashSetCount">
                        ${[10, 15, 20, 30].map(function(c) { return html`<option value="${c}"${f.count === c ? html` selected` : ''}>${c} cartes</option>`; })}
                    </select></span>
                ${rienDeNeuf && !busy
                    ? html`<button type="button" class="btn btn-primaire btn-sm" data-action="flashOpen" data-args="${attr([String(n), fp, true])}" title="Rien de nouveau à cibler : la répétition espacée du paquet existant suffit, et elle est gratuite">${ico('arrows-clockwise', { taille: 16 })}Réviser le paquet existant</button>
                        <button type="button" class="btn btn-fantome btn-sm" data-action="flashGenerateQuandMeme">Générer quand même</button>`
                    : boutonIA({ action: 'flashGenerate', label: 'Générer avec l\'IA', verrou: 'Générer des cartes · abonnement', occupe: busy, enCours: 'Génération',
                        // Tant qu'une carte attend d'être révisée, l'action pleine de l'écran est « Voir la réponse » :
                        // fabriquer d'autres cartes est un appel payant, pas le geste du moment.
                        classe: carte ? 'btn-secondaire' : 'btn-primaire' })}
                ${i.admin() ? html`<button type="button" class="btn btn-fantome btn-sm" data-action="openLots" data-args="${attr(['flashcards'])}" title="Traiter plusieurs items d'un coup par l'API par lots : moitié prix, résultats à appliquer quand ils arrivent">${ico('package', { taille: 16 })}En lot</button>` : ''}
            </div>
            ${f.mode === 'weak' && frais ? html`<p class="qcm-gen-note ${rienDeNeuf ? 'qcm-gen-note--ok' : 'qcm-gen-note--neuf'}">
                ${rienDeNeuf ? html`${ico('check-circle', { taille: 16 })}Aucune nouvelle lacune ni erreur depuis le ${i.fmtDate(deck.lastWeakAt)} : révise le paquet existant, c'est gratuit et la répétition espacée fait le travail entre deux J.`
                    : html`${ico('warning-circle', { taille: 16 })}Du nouveau depuis le ${i.fmtDate(deck.lastWeakAt)} : ${frais.errors ? pluriel(frais.errors, 'erreur', 'erreurs') + ', ' : ''}${pluriel(frais.gaps - frais.errors, 'lacune', 'lacunes')}, ${pluriel(frais.failed, 'réponse ratée', 'réponses ratées')}. Une génération ciblée se justifie.`}</p>` : ''}
            <p class="qcm-gen-source">
                ${pdf ? html`${ico('file-pdf', { taille: 16 })}Fiche EDN complète disponible : l'IA la lit en entier.`
                     : html`${ico('warning-circle', { taille: 16 })}Pas de fiche PDF pour cet item sur le serveur : le plan et les notions des questions serviront de source.`}
                ${f.mode === 'weak'
                    ? html` Ciblage : ${(function() { var g = i.itemGaps(n, f.part).slice(0, 4).map(function(x) { return x.titre; }),
                        w = i.weakestNotions(n, 5).map(function(x) { return x.title + ' (' + pct(x.mastery) + ' %)'; });
                        return g.concat(w).join(' · ') || 'aucun QCM répondu ni active recall analysé sur cet item — les cartes suivront la fiche, comme en découverte'; })()}.`
                    : html` Les cartes suivent le plan de la fiche${portee ? ', pour la partie « ' + portee + ' »' : ''}, rang A en priorité.`}
            </p>
        </section>`}

        ${!deck || !deck.cards.length
            ? html`<div class="vide">${ico('cards-three')}<h2>Aucune carte pour l'instant</h2><p>${f.parts ? 'Aucune de ces sous-parties n\'a encore de cartes : ouvre-en une seule pour en créer.' : 'Choisis un mode ci-dessus et lance la génération : le paquet se remplit, puis la répétition espacée le ramène toute seule.'}</p></div>`
            : (carte ? html`${enteteCarte(carte, f, f.parts ? nomPartie(n, (f.src || {})[carte.id]) : null)}
                ${carteFlash(carte, f)}
                <div id="qcm-flash-voir" class="qcm-flash-voir"${f.flipped ? html` hidden` : ''}>
                    <button type="button" class="btn btn-primaire btn-lg" data-action="flashFlip">${ico('arrows-clockwise', { taille: 18 })}Voir la réponse</button>
                </div>
                <div id="qcm-flash-notes" class="qcm-notes"${f.flipped ? '' : html` hidden`} role="group" aria-label="Noter cette carte">${notesCarte(carte)}</div>
                <div class="qcm-flash-pied">
                    <button type="button" class="qcm-lien qcm-lien--danger" data-action="flashDeleteCard" data-args="${attr([carte.id])}">${ico('trash-simple', { taille: 16 })}Supprimer cette carte</button>
                </div>`
            : finFlash(n, f, deck, fp))}
    </div>`;
}

function finFlash(n, f, deck, fp) {
    var i = I();
    var suivante = deck.cards.map(function(c) { return c.due || 0; }).filter(function(d) { return d > Date.now(); }).sort(function(a, b) { return a - b; })[0];
    var moyenne = f.results.length ? f.results.reduce(function(a, r) { return a + r.score; }, 0) / f.results.length : null;
    return html`<div class="qcm-fin qcm-fin--flash">
        ${f.results.length ? html`<div class="qcm-fin-score">
                <p class="qcm-fin-chiffre num">${pct(moyenne)} %</p>
                <p class="qcm-fin-mot">${motMaitrise(moyenne)}</p>
                <p class="qcm-fin-note num">${pluriel(f.results.length, 'carte révisée', 'cartes révisées')} · ${pluriel(f.results.filter(function(r) { return r.score < 1; }).length, 'à retravailler', 'à retravailler')}</p>
            </div>`
            : html`<div class="qcm-fin-score"><p class="qcm-fin-mot">${ico('check-circle', { taille: 24 })}Paquet à jour</p><p class="qcm-fin-note num">${suivante ? 'Prochaine révision le ' + i.fmtDate(suivante) + '.' : 'Aucune échéance en attente.'}</p></div>`}
        <div class="qcm-fin-suite">
            <button type="button" class="btn btn-secondaire" data-action="flashOpen" data-args="${attr([String(n), fp, true])}">${ico('arrows-clockwise', { taille: 18 })}Réviser tout le paquet</button>
            <button type="button" class="btn btn-fantome" data-action="qcmStartSession" data-args="${attr([String(n), 'adaptive', fp, 10])}">${ico('lightning', { taille: 18 })}Vérifier en QCM</button>
        </div>
    </div>`;
}

// ================================================================= active recall

function planRecall(r, liste) {
    return html`<ol class="qcm-plan" aria-label="Plan de la fiche">${liste.map(function(p, k) {
        var fait = null; r.results.some(function(x) { if (x.sec === p) { fait = x; return true; } return false; });
        var etat = k === r.idx ? 'encours' : fait ? (fait.score >= 1 ? 'ok' : fait.score > 0 ? 'partiel' : 'rate') : 'avenir';
        var mot = k === r.idx ? 'en cours' : fait ? (fait.score >= 1 ? 'acquis' : fait.score > 0 ? 'partiel' : 'oublié') : 'à venir';
        return html`<li class="qcm-plan-pas qcm-plan-pas--${etat}"><span class="num">${k + 1}</span><span class="sr-only">${p.label} : ${mot}</span></li>`;
    })}</ol>`;
}

function ficheRecall(sec, etats) {
    var MOTS = { ok: ['Restitué', 'ok'], partiel: ['Partiel : la valeur exacte manque', 'partiel'], manque: ['Non restitué', 'manque'] };
    return html`<ul class="qcm-fiche">${(sec.items || []).map(function(pt, k) {
        var e = etats ? etats[k] : null, m = MOTS[e];
        return html`<li class="qcm-fiche-pt${m ? ' qcm-fiche-pt--' + m[1] : ''}">
            ${m ? html`<span class="qcm-fiche-etat" title="${m[0]}">${e === 'ok' ? ico('check-circle', { taille: 16 }) : e === 'partiel' ? ico('warning-circle', { taille: 16 }) : ico('x', { taille: 16 })}<span class="sr-only">${m[0]}</span></span>` : html`<span class="qcm-fiche-puce" aria-hidden="true"></span>`}
            <span>${pt}</span></li>`;
    })}</ul>
    ${(sec.aRetenir || []).length ? html`<p class="qcm-fiche-retenir"><strong>À retenir</strong> · ${sec.aRetenir.join(' · ')}</p>` : ''}
    ${sec.piege ? html`<p class="qcm-fiche-piege"><strong>Piège EDN</strong> · ${sec.piege}</p>` : ''}`;
}

function vueRecall() {
    var i = I(), r = state.qcm.recall; if (!r) return vueRegistre();
    var n = r.n, portee = nomPartie(n, r.part);
    var tete = html`<header class="qcm-entete qcm-entete--exo">
        <div class="qcm-entete-texte">
            ${retourItem(n)}
            <h1>${ico('brain', { taille: 24 })}Active recall · ${i.itemTitle(n)}</h1>
            <div class="qcm-item-puces">
                ${portee ? html`<span class="puce puce-accent">${portee}</span>` : ''}
                ${r.pedia ? html`<span class="puce puce-pedia">Renforcement pédiatrie</span>` : ''}
                ${r.all ? html`<span class="puce puce-sans-point num">Fiche du podcast${r.explicatif ? ' explicatif' : ''} · ${pluriel(i.recallList(r).length, 'point à réciter', 'points à réciter')}</span>` : ''}
                ${r.offline ? html`<span class="puce puce-warn">Hors ligne</span>` : ''}
            </div>
            ${r.all ? (function() { var nc = r.all.filter(function(sec) { return window.isRangC(r.n, sec.label); }).length;
                return nc ? html`<button type="button" class="qcm-lien" data-action="recallShowRangC" data-args="${attr([!r.showRangC])}">${pluriel(nc, 'section classée', 'sections classées')} rang C (hors programme) — ${r.showRangC ? 'masquer' : 'afficher pour les remettre au programme'}</button>` : ''; })() : ''}
        </div>
    </header>${noticeHtml()}`;

    if (r.loading) return html`<div class="qcm-recall">${tete}<div class="squelettes"><div class="squelette squelette-titre"></div><div class="squelette squelette-bloc"></div><div class="squelette squelette-bloc"></div></div><p class="sr-only" role="status">Recherche de la fiche à suivre du podcast…</p></div>`;
    if (r.error) return html`<div class="qcm-recall">${tete}<div class="qcm-alerte" role="alert">${ico('warning-circle', { taille: 20 })}<div><p>${r.error}</p></div></div></div>`;
    if (r.noFiche) {
        var busyF = state.qcm.busy === 'recall_fiche';
        return html`<div class="qcm-recall">${tete}
            <div class="vide qcm-vide-fiche">
                ${ico('file-text')}
                <h2>L'active recall se fait sur la fiche à suivre du podcast</h2>
                <p>Chaque section de la fiche que tu suis pendant l'écoute devient un point à réciter de mémoire, dans l'ordre du podcast ; ensuite l'IA compare tes notes à la fiche, point par point.</p>
                <p>${r.script ? 'Le script du podcast' + (r.script.explicatif ? ' explicatif' : '') + ' existe : il reste à écrire la fiche, ce qui prend de trente secondes à deux minutes.'
                    : 'Il n\'y a pas encore de podcast pour ' + (r.pedia ? 'ce renforcement pédiatrie' : (r.part != null ? 'cette sous-partie' : 'cet item')) + ' : commence par son script.'}</p>
                ${r.script ? boutonIA({ action: 'recallCreateFiche', label: 'Créer la fiche à suivre', verrou: 'Fiche à suivre · abonnement', occupe: busyF, enCours: 'Rédaction de la fiche' })
                    : html`<button type="button" class="btn btn-secondaire" data-action="recallOpenPodcast">${ico('headphones', { taille: 18 })}Ouvrir le podcast</button>`}
            </div></div>`;
    }

    var liste = i.recallList(r), sec = liste[r.idx];
    if (!sec) return html`<div class="qcm-recall">${tete}${finRecall(r, liste)}</div>`;

    var dct = state.qcm.dict || { on: false, interim: '', error: '' };
    return html`<div class="qcm-recall">${tete}
        <div class="qcm-recall-etat">
            ${avancement(r.idx, liste.length, { label: 'Avancement de l\'active recall', texte: 'Point ' + (r.idx + 1) + ' sur ' + liste.length, id: 'qcm-compteur-recall' })}
            <span class="puce puce-sans-point">${sec.kind === 'recap' ? 'Récap des chiffres' : 'Section ' + (sec.idx + 1) + ' du podcast'}${sec.paragraphes ? ' · ¶ ' + sec.paragraphes : ''}</span>
        </div>
        ${planRecall(r, liste)}
        <article class="qcm-point carte">
            <div class="carte-corps">
                <p class="qcm-point-sur">Qu'as-tu retenu sur…</p>
                <h2>${sec.label}${window.isRangC(r.n, sec.label) ? html` <span class="puce puce-sans-point">rang C</span>` : ''}</h2>
                <div class="qcm-consigne">
                    <p class="qcm-consigne-tete">${ico('target', { taille: 16 })}Ce qu'on attend de toi</p>
                    <p>${sec.consigne}</p>
                    <p class="qcm-consigne-nb num">${sec.nb} ${sec.kind === 'recap' ? 'valeurs' : 'points'} dans la fiche${sec.paragraphes ? ' · paragraphes ' + sec.paragraphes + ' du podcast' : ''}</p>
                </div>
                ${r.revealed ? html`
                    ${r.notes ? html`<div class="qcm-tes-notes"><p class="qcm-tes-notes-tete">Ce que tu as noté</p><p>${r.notes}</p></div>` : ''}
                    <div class="qcm-la-fiche">
                        <p class="qcm-la-fiche-tete">${ico('file-text', { taille: 16 })}La fiche dit</p>
                        ${ficheRecall(sec, null)}
                    </div>
                    <div class="qcm-autoeval">
                        <p class="qcm-autoeval-tete" id="qcm-autoeval-tete">Honnêtement, tu avais retenu…</p>
                        <div class="qcm-notes" role="group" aria-labelledby="qcm-autoeval-tete">
                            <button type="button" class="qcm-note qcm-note-again" data-action="recallRate" data-args="${attr([0])}"><span class="qcm-note-mot">Oublié</span><span class="qcm-note-quand">peu ou rien</span></button>
                            <button type="button" class="qcm-note qcm-note-hard" data-action="recallRate" data-args="${attr([0.5])}"><span class="qcm-note-mot">Partiel</span><span class="qcm-note-quand">l'idée, pas les détails</span></button>
                            <button type="button" class="qcm-note qcm-note-good" data-action="recallRate" data-args="${attr([1])}"><span class="qcm-note-mot">Acquis</span><span class="qcm-note-quand">l'essentiel et les chiffres</span></button>
                        </div>
                        <!-- Le libellé dit ce que le clic fera, comme en fin de séance : un bouton qui bascule
                             ne peut pas garder le même mot dans les deux états. -->
                        <button type="button" class="qcm-lien qcm-lien--sobre" data-action="recallToggleRangC" data-args="${attr([r.all.indexOf(sec)])}" title="Le programme R2C ne connaît que les rangs A et B : classer cette notion « rang C » la retire de l'active recall, de ta maîtrise et des points à retravailler">${window.isRangC(r.n, sec.label) ? 'Classé rang C — remettre au programme' : 'Rang C — hors programme, ne pas compter'}</button>
                    </div>`
                : html`
                    <div class="qcm-dictee">
                        <button type="button" class="btn ${dct.on ? 'btn-danger' : 'btn-secondaire'} btn-sm${dct.on ? ' qcm-ecoute' : ''}" data-action="dictToggle" aria-pressed="${dct.on ? 'true' : 'false'}" title="Dicte ta réponse : le texte est écrit dans le champ, avec les termes médicaux et les chiffres remis en forme">
                            ${dct.on ? ico('microphone-slash', { taille: 16 }) : ico('microphone', { taille: 16 })}${dct.on ? 'Arrêter la dictée' : 'Dicter'}</button>
                        ${dct.on ? html`<span class="qcm-dictee-etat" role="status">J'écoute…</span><span id="dict-interim" class="qcm-dictee-interim">${dct.interim}</span>`
                            : html`<span class="qcm-dictee-etat">ou écris à la main ci-dessous</span>`}
                        ${dct.error ? html`<p class="qcm-dictee-erreur" role="alert">${ico('warning-circle', { taille: 16 })}${dct.error}</p>` : ''}
                    </div>
                    <div class="champ qcm-notes-champ">
                        <label for="recall-notes" class="sr-only">Ce que tu retiens</label>
                        <textarea id="recall-notes" rows="6" data-input-action="recallNotes" placeholder="Écris ici, de mémoire, tout ce que tu retiens : chiffres, seuils, critères, conduite à tenir. En fin de séance, l'IA compare tes notes à la fiche, point par point.">${r.notes}</textarea>
                    </div>
                    <div class="qcm-point-actions">
                        <button type="button" class="btn btn-primaire" data-action="recallReveal">${ico('eye', { taille: 18 })}Révéler la fiche</button>
                        <button type="button" class="btn btn-fantome" data-action="recallSkip">Passer ce point</button>
                    </div>`}
            </div>
        </article>
    </div>`;
}

function finRecall(r, liste) {
    var i = I();
    var moyenne = r.results.length ? r.results.reduce(function(a, x) { return a + x.score; }, 0) / r.results.length : null;
    var avecNotes = r.results.filter(function(x) { return (x.notes || '').trim(); }).length;
    var moyIA = r.ai ? (function() { var v = r.results.filter(function(x) { return x.ai; }); return v.length ? v.reduce(function(a, x) { return a + x.ai.score; }, 0) / v.length : null; })() : null;
    var erreurs = [];
    r.results.forEach(function(x) { ((x.ai && x.ai.erreurs) || []).forEach(function(e) { erreurs.push({ point: x.sec.label, e: e, self: x.score }); }); });
    var busy = state.qcm.busy === 'recall_ai';

    return html`${erreurs.length ? html`<section class="qcm-erreurs" role="region" aria-label="Erreurs à corriger">
        <h2>${ico('warning-circle', { taille: 20 })}${pluriel(erreurs.length, 'erreur à corriger', 'erreurs à corriger')} maintenant</h2>
        <p class="qcm-erreurs-note">Tu as écrit quelque chose de faux : une erreur mémorisée coûte plus cher qu'un oubli. Relis le vrai, puis vérifie-le en QCM.</p>
        <ul>${erreurs.map(function(a) {
            return html`<li class="qcm-erreur">
                <p class="qcm-erreur-point">${a.point}${a.self >= 1 ? ' — tu t\'étais évalué « acquis »' : ''}</p>
                ${a.e.ecrit ? html`<p class="qcm-erreur-ecrit">${a.e.ecrit}</p>` : ''}
                <p class="qcm-erreur-vrai">${ico('check', { taille: 16 })}${a.e.correct}</p>
                ${a.e.pourquoi ? html`<p class="qcm-erreur-pourquoi">${a.e.pourquoi}</p>` : ''}
            </li>`;
        })}</ul>
    </section>` : ''}
    <div class="qcm-fin qcm-fin--recall">
        ${r.results.length ? html`<div class="qcm-fin-score">
                <p class="qcm-fin-chiffre num">${pct(moyenne)} %</p>
                <p class="qcm-fin-mot">auto-évaluation</p>
                <p class="qcm-fin-note num">${pluriel(r.results.length, 'point noté', 'points notés')}</p>
                ${moyIA != null ? html`<p class="qcm-fin-ia num" title="Moyenne des sections corrigées. Chacune vaut la part des points de sa fiche que tu as restitués.">${pct(moyIA)} % des points de la fiche${r.ai && r.ai.source === 'local' ? html`<span class="qcm-fin-ia-src"> · correction locale, sans IA</span>` : ''}</p>` : ''}
            </div>
            <div class="qcm-fin-points">
                ${r.results.map(function(x) {
                    var ai = x.ai, faute = ai && ai.erreurs && ai.erreurs.length, cnt = i.recallItemCount(ai && ai.items);
                    return html`<div class="qcm-fin-point${faute ? ' qcm-fin-point--erreur' : ''}">
                        <div class="qcm-fin-point-tete">
                            <span class="qcm-fin-point-nom">${faute ? html`<span class="puce puce-danger">Erreur</span>` : ''}${x.sec.label}${!(x.notes || '').trim() ? html` <span class="qcm-fin-point-rien">(rien écrit)</span>` : ''}</span>
                            <span class="qcm-fin-point-scores">
                                ${cnt.total ? html`<span class="num qcm-fin-point-cnt" title="Points de la fiche restitués${cnt.partiel ? ' — dont ' + pluriel(cnt.partiel, 'point à moitié', 'points à moitié') + ', qui comptent pour une demi-part' : ''}">${cnt.ok}${cnt.partiel ? html` + ${cnt.partiel}<span class="qcm-fin-point-demi">½</span>` : ''} / ${cnt.total}</span>` : ''}
                                ${puceScore(x.score)}
                                ${ai ? html`<span class="num qcm-fin-point-ia" title="${cnt.total ? 'Part des points de la fiche que tu as restitués, les points à moitié comptant pour une demi-part' + (faute ? ', moins le malus d\'erreur factuelle' : '') : 'Score donné par l\'IA'}">IA ${pct(ai.score)} %</span>` : ''}
                            </span>
                        </div>
                        ${cnt.total ? html`<div class="qcm-fin-point-fiche">${ficheRecall({ items: x.sec.items, aRetenir: [], piege: '' }, ai.items)}</div>` : ''}
                        ${ai && (ai.manques.length || ai.acquis.length) ? html`<div class="qcm-fin-point-bilan">
                            ${ai.acquis.length ? html`<p class="qcm-acquis"><strong>Acquis :</strong> ${ai.acquis.join(' · ')}</p>` : ''}
                            ${ai.manques.length ? html`<div class="qcm-manques"><p><strong>À retenir :</strong></p><ul>${ai.manques.map(function(m) { return html`<li>${m}</li>`; })}</ul></div>` : ''}
                        </div>` : ''}
                        <button type="button" class="qcm-lien qcm-lien--sobre" data-action="recallToggleRangC" data-args="${attr([r.all.indexOf(x.sec)])}" title="Hors programme R2C : retiré de l'active recall, de ta maîtrise et des points à retravailler">${window.isRangC(r.n, x.sec.label) ? 'Classé rang C — remettre au programme' : 'Rang C — hors programme'}</button>
                    </div>`;
                })}
            </div>`
            : html`<p class="qcm-bloc-vide">Aucune section à réviser dans ce périmètre.</p>`}

        ${r.results.length && !r.ai ? html`<div class="qcm-analyse">
            <p>${ico('sparkle', { taille: 16 })}L'IA relit tes notes, coche un par un les points de la fiche que tu as restitués, note chaque section, liste ce qui manque et ajoute tes lacunes à ton profil.${avecNotes < r.results.length ? ' ' + avecNotes + ' point' + (avecNotes > 1 ? 's' : '') + ' sur ' + r.results.length + ' portent des notes.' : ''}</p>
            ${boutonIA({ action: 'recallAnalyse', label: 'Analyser mes réponses', verrou: 'Analyse IA · abonnement', occupe: busy, desactive: !avecNotes, enCours: 'Analyse',
                aideDesactive: 'Écris ce que tu retiens avant de révéler un point : sans notes, l\'IA n\'a rien à relire.' })}
            <p class="qcm-analyse-locale">Ou tout de suite, sans IA et sans réseau : le correcteur local coche les points de la fiche à partir du lexique médical — abréviations, synonymes, unités, négations, orthographe approximative. Il ne signale que ce qui se prouve sans comprendre : un chiffre contredit, une affirmation retournée.</p>
            <button type="button" class="btn btn-secondaire" data-action="recallCorrigerLocal"${avecNotes ? '' : html` disabled`}>${ico('list-checks', { taille: 18 })}Corriger sans IA</button>
        </div>` : ''}
        ${r.ai && r.ai.pointsFaibles.length ? html`<section class="qcm-bloc qcm-bloc--faibles">
            <h2>Points faibles prioritaires</h2>
            <ul class="qcm-lacunes">${r.ai.pointsFaibles.map(function(pf) {
                return html`<li class="qcm-lacune${pf.type === 'erreur' ? ' qcm-lacune--erreur' : ''}">
                    <span class="puce ${pf.type === 'erreur' ? 'puce-danger' : 'puce-warn'}">${pf.type === 'erreur' ? 'Erreur' : 'Oubli'}</span>
                    <span class="qcm-lacune-texte"><strong>${pf.titre}</strong>${pf.detail ? html` — ${pf.detail}` : ''}</span></li>`;
            })}</ul>
        </section>` : ''}
        <div class="qcm-fin-suite">
            ${r.ai && r.ai.pointsFaibles.length ? html`<button type="button" class="btn btn-secondaire" data-action="coursOpen" data-args="${attr([String(r.n), r.part == null ? '' : r.part, 'recall'])}">${ico('graduation-cap', { taille: 18 })}Mini-cours sur ces points</button>
                <button type="button" class="btn btn-fantome" data-action="flashOpen" data-args="${attr([String(r.n), r.part == null ? '' : r.part, false])}">${ico('cards-three', { taille: 18 })}Flashcards ciblées</button>` : ''}
            <button type="button" class="btn btn-fantome" data-action="recallRestart">${ico('arrow-counter-clockwise', { taille: 18 })}Recommencer</button>
            <button type="button" class="btn btn-primaire" data-action="qcmStartSession" data-args="${attr([String(r.n), 'adaptive', r.part == null ? '' : r.part, 10])}">${ico('lightning', { taille: 18 })}Vérifier en QCM</button>
        </div>
    </div>`;
}

// ================================================================= mini-cours IA

function vueCours() {
    var i = I(), c = state.qcm.cours; if (!c) return vueRegistre();
    var n = c.n, portee = nomPartie(n, c.part);
    var garde = state.learning.cours[i.deckKey(n, c.part)];
    var busy = state.qcm.busy === 'cours';
    var pdf = window.LOCAL_FICHES && window.LOCAL_FICHES[n];
    var cst = state.collegeStatus ? state.collegeStatus[n] : null;
    return html`<div class="qcm-cours">
        <header class="qcm-entete qcm-entete--exo">
            <div class="qcm-entete-texte">
                ${retourItem(n)}
                <h1>${ico('graduation-cap', { taille: 24 })}Mini-cours ciblé · ${i.itemTitle(n)}</h1>
                <div class="qcm-item-puces">${portee ? html`<span class="puce puce-accent">${portee}</span>` : ''}</div>
                <p class="qcm-cours-source">
                    ${pdf ? html`${ico('file-pdf', { taille: 16 })}Source : la fiche EDN complète, lue en entier par l'IA.`
                          : html`${ico('warning-circle', { taille: 16 })}Pas de fiche PDF pour cet item sur le serveur : le cours ne sera pas ancré sur ta fiche.`}
                </p>
                <p class="qcm-cours-source">
                    ${!cst ? html`${ico('circle-notch', { taille: 16, classe: 'qcm-tourne' })}Recherche des extraits de collège…`
                        : (cst.sources && cst.sources.length) ? html`${ico('book-open', { taille: 16 })}${pluriel(cst.sources.length, 'collège', 'collèges')} : ${i.collegeLabel(cst.sources.slice(0, 4))} — pour expliquer ce que la fiche résume${portee ? ' (passages de la sous-partie seulement)' : ''}.`
                        : (cst.colleges_count && cst.can_extract) ? html`${ico('book-open', { taille: 16 })}Pas encore d'extrait pour cet item : le serveur l'extraira des collèges à la génération.`
                        : html`${ico('book-open', { taille: 16 })}Aucun collège indexé pour cet item : le cours s'appuiera sur la fiche seule.`}
                </p>
            </div>
        </header>
        ${noticeHtml()}
        <section class="qcm-gen">
            <div class="qcm-gen-ligne">
                <div class="qcm-gen-points">
                    <p class="qcm-gen-label">Points à expliquer${c.points.length ? ' (' + c.points.length + ')' : ''}</p>
                    ${c.points.length ? html`<div class="qcm-gen-puces">${c.points.map(function(p) {
                        return html`<span class="puce ${p.type === 'erreur' ? 'puce-danger' : 'puce-sans-point'}" title="${p.detail || ''}">${p.titre}</span>`;
                    })}</div>` : html`<p class="qcm-gen-note">Aucune lacune d'active recall : le cours portera sur tes notions les moins maîtrisées${i.weakestNotions(n, 3).length ? ' (' + i.weakestNotions(n, 3).map(function(x) { return x.title; }).join(', ') + '…)' : ''}.</p>`}
                </div>
                ${boutonIA({ action: 'coursGenerate', label: garde ? 'Réécrire le mini-cours' : 'Écrire le mini-cours', verrou: 'Mini-cours · abonnement', occupe: busy, enCours: 'Rédaction' })}
            </div>
        </section>
        ${garde && garde.cours ? articleCours(garde, n, c) : (busy ? '' : html`<div class="vide">${ico('graduation-cap')}<h2>Pas encore de mini-cours</h2><p>Lance la rédaction : l'IA reprend tes erreurs et tes notions faibles, une section par point.</p></div>`)}
    </div>`;
}

function articleCours(garde, n, c) {
    var i = I(), k = garde.cours;
    var CIBLES = { qcm: ['crosshair', 'Cible une erreur de QCM'], flash: ['cards-three', 'Cible une flashcard non acquise'], recall: ['brain', 'Cible une lacune d\'active recall'] };
    return html`<article class="qcm-cours-texte carte"><div class="carte-corps">
        <header class="qcm-cours-tete">
            <p class="qcm-cours-quand num">Écrit le ${i.fmtDate(garde.updated)}${garde.source === 'pdf' ? ' d\'après la fiche EDN' : ''}${garde.college && garde.college.length ? ' + collège ' + i.collegeLabel(garde.college) : ''}</p>
            <h2>${k.titre}</h2>
            ${k.intro ? html`<p class="qcm-cours-intro">${k.intro}</p>` : ''}
        </header>
        ${(k.sections || []).map(function(sec, idx) {
            var cible = CIBLES[sec.origine];
            return html`<section class="qcm-cours-section">
                <h3><span class="qcm-cours-num num" aria-hidden="true">${idx + 1}</span>${sec.titre} ${sec.rang === 'AB' ? html`${puceRang('A')}${puceRang('B')}` : puceRang(sec.rang)}</h3>
                ${cible ? html`<p class="qcm-cours-cible">${ico(cible[0], { taille: 16 })}${cible[1]}</p>` : ''}
                <p class="qcm-cours-contenu">${sec.contenu}</p>
                ${(sec.aRetenir || []).length ? html`<div class="qcm-cours-retenir"><p>À retenir</p><ul>${sec.aRetenir.map(function(a) { return html`<li>${a}</li>`; })}</ul></div>` : ''}
                ${sec.piege ? html`<p class="qcm-cours-piege"><strong>Piège EDN</strong> — ${sec.piege}</p>` : ''}
            </section>`;
        })}
        ${k.conclusion ? html`<footer class="qcm-cours-fin"><p>${k.conclusion}</p></footer>` : ''}
        <div class="qcm-fin-suite">
            <button type="button" class="btn btn-secondaire" data-action="flashOpen" data-args="${attr([String(n), c.part == null ? '' : c.part, false])}">${ico('cards-three', { taille: 18 })}Flashcards sur ces points</button>
            <button type="button" class="btn btn-fantome" data-action="qcmStartSession" data-args="${attr([String(n), 'adaptive', c.part == null ? '' : c.part, 10])}">${ico('lightning', { taille: 18 })}Vérifier en QCM</button>
        </div>
    </div></article>`;
}

// ================================================================= « Extraire en PDF »
// La fenêtre entière, cadre compris : window.MODALES[type] est le point d'entrée prévu par renderModal pour les
// fenêtres refaites, et le cadre historique posait au-dessus de ce corps un titre en Font Awesome, en graisse
// 900 et en palette Tailwind orange. Le titre appartient maintenant à la fenêtre, comme son corps.
export function qcmLivreFenetre() {
    return html`<div class="qcm-fenetre" data-action="qcmFermerVoile">
        <div class="qcm-fenetre-boite" role="dialog" aria-modal="true" aria-labelledby="qcm-livre-titre" tabindex="-1">
            <h2 id="qcm-livre-titre" class="qcm-fenetre-titre">${ico('file-pdf', { taille: 22 })}Extraire les QCM en PDF</h2>
            ${qcmLivreCorps()}
        </div>
    </div>`;
}

// Le corps de la fenêtre, en data-action.
export function qcmLivreCorps() {
    var i = I(), L = state.qcm.livre || { colleges: [], ped: true }, cols = i.livreParCollege();
    var pris = cols.filter(function(c) { return L.colleges.indexOf(c.nom) >= 0; });
    var nbQ = pris.reduce(function(a, c) { return a + c.q; }, 0);
    var nbI = pris.reduce(function(a, c) { return a + c.items; }, 0);
    var nbP = L.ped ? pris.reduce(function(a, c) { return a + c.ped; }, 0) : 0;
    // Mesuré sur la banque : en compact, une page porte 4,7 questions avec leurs corrections et pèse environ
    // 10 Ko ; en confort, 1,45 question par page. Annoncer des pages ET des méga-octets évite de lancer une
    // impression de mille pages sans le savoir.
    var compact = L.densite !== 'confort', total = nbQ + nbP;
    var pages = Math.max(1, Math.round(total / (compact ? 4.7 : 1.45)) + nbI + 2);
    var mo = Math.max(0.1, total * (compact ? 9.9 : 12.1) / 1024);
    var lourd = total > 1500;
    return html`<div class="qcm-livre">
        <p class="qcm-livre-intro">Toutes les questions réunies en un livre : par collège principal, puis par item ; les questions d'un item d'abord, ses corrections juste après. Une section Pédiatrie reprend à la fin les questions marquées pédiatriques — elles y sont donc deux fois, c'est voulu.</p>
        <div class="qcm-livre-tete">
            <p id="qcm-livre-label">Collèges principaux</p>
            <span class="qcm-livre-tout">
                <button type="button" class="btn btn-fantome btn-sm" data-action="livreTousColleges" data-args="${attr([true])}">Tout</button>
                <button type="button" class="btn btn-fantome btn-sm" data-action="livreTousColleges" data-args="${attr([false])}">Aucun</button>
            </span>
        </div>
        <div class="qcm-livre-liste" role="group" aria-labelledby="qcm-livre-label">
            ${cols.length ? cols.map(function(c) {
                var on = L.colleges.indexOf(c.nom) >= 0;
                return html`<label class="qcm-livre-col"><input type="checkbox"${on ? html` checked` : ''} data-change-action="livreBasculerCollege" data-args="${attr([c.nom])}">
                    <span class="qcm-livre-nom">${c.nom}</span>
                    <span class="qcm-livre-nb num">${pluriel(c.items, 'item', 'items')} · ${pluriel(c.q, 'question', 'questions')}${c.ped ? ' · ' + c.ped + ' en pédiatrie' : ''}</span></label>`;
            }) : html`<p class="qcm-bloc-vide">L'index des QCM n'est pas encore chargé.</p>`}
        </div>
        <label class="qcm-coche"><input type="checkbox"${L.ped ? html` checked` : ''} data-change-action="livrePed"><span>Ajouter la section Pédiatrie à la fin</span></label>
        <div class="qcm-livre-mep">
            <p id="qcm-livre-mep">Mise en page</p>
            <div class="segments" role="tablist" aria-labelledby="qcm-livre-mep">
                ${[['compact', 'Compact', 'deux colonnes, texte serré'], ['confort', 'Confort', 'une colonne, texte aéré']].map(function(d) {
                    var on = (L.densite !== 'confort') === (d[0] === 'compact');
                    return html`<button type="button" role="tab" aria-selected="${on ? 'true' : 'false'}" data-action="livreDensite" data-args="${attr([d[0]])}" title="${d[2]}">${d[1]}</button>`;
                })}
            </div>
        </div>
        <p class="qcm-livre-poids${lourd ? ' qcm-livre-poids--lourd' : ''}">
            <strong class="num">${pluriel(nbI, 'item', 'items')} · ${pluriel(nbQ, 'question', 'questions')}${nbP ? ' + ' + nbP + ' en pédiatrie' : ''}</strong>
            <span class="num"> — environ ${pages.toLocaleString('fr-FR')} pages, ${mo < 10 ? mo.toFixed(1) : Math.round(mo)} Mo.</span>
            ${compact ? '' : html`<span class="num"> En compact, ce serait environ ${Math.round(total / 4.7 + nbI + 2).toLocaleString('fr-FR')} pages.</span>`}
            ${lourd ? ' C\'est un très gros document : compte une bonne minute de composition, et autant à l\'impression. Mieux vaut le sortir collège par collège.' : ''}
        </p>
        <div class="qcm-livre-actions">
            <button type="button" class="btn btn-fantome" data-action="qcmFermerFenetre">Annuler</button>
            <button type="button" class="btn btn-primaire" data-action="livreImprimer"${pris.length ? '' : html` disabled aria-disabled="true"`}>${ico('file-pdf', { taille: 18 })}Ouvrir le livre</button>
        </div>
    </div>`;
}

// ================================================================= le rendu de l'onglet

export function squeletteQcm() {
    return html`<div id="qcm-root" class="qcm">
        <div class="squelettes" aria-hidden="true">
            <div class="squelette squelette-titre"></div>
            <div class="squelette squelette-bloc"></div>
            <div class="squelette squelette-bloc"></div>
        </div>
        <p class="sr-only" role="status">Chargement de l'onglet QCM…</p>
    </div>`;
}

// ================================================================= Training : les sujets d'EDN.fr (privés, administrateur)
// Les sujets de la page Annales d'EDN.fr, déposés dans edn_data/annales/ par edn_annales_scraper.py. Une sous-section
// par type d'épreuve, chacune avec sa teinte (--ep-*, voir tokens.css) : un point devant le nom, un fond mélangé à la
// carte, jamais un texte ni un bouton — le nom est toujours écrit. Classés par matière, par item ou par date ; joués en
// épreuve : on répond à tout, la correction vient à la fin.

var TYPES_ANNALES = {
    ANNALS: 'Annale EDN', ECNI_BLANK: 'EDN Blanc', CONTEST: 'Concours EDN', QUIZZ: 'EDNiQuizz', CONFTUBE: 'Conftube',
    MASTERCLASS: 'Masterclass', LCA: 'LCA', IMAGING: 'Imagerie', EASY_DP: 'Easy DP', MINI_DP: 'Mini DP transversaux',
    KFP: 'KFP', TCS: 'TCS', ECOS: 'ECOS',
};
// Une phrase par type : ce qu'on y trouve, pas une promesse.
var DESCRIPTIONS_TYPES = {
    ANNALS: 'Les sujets des EDN des années passées.',
    ECNI_BLANK: 'Des épreuves blanches complètes, en conditions réelles.',
    CONTEST: 'Les concours blancs, en épreuve complète.',
    QUIZZ: 'Des quiz courts qui mêlent les formats.',
    CONFTUBE: 'Une vidéo de cours, puis ses questions.',
    MASTERCLASS: 'Une séance thématique approfondie, et ses questions.',
    LCA: 'Lecture critique d\'article.',
    IMAGING: 'Des questions construites autour d\'une image.',
    EASY_DP: 'Des dossiers progressifs courts.',
    MINI_DP: 'De petits dossiers qui croisent plusieurs spécialités.',
    KFP: 'Key Feature Problems : les décisions clés d\'un cas.',
    TCS: 'Tests de concordance de script : raisonner face à l\'incertitude.',
};
var TYPES_DOSSIER = { CLINICAL: 'Dossier clinique', KFP: 'KFP', TCS: 'TCS', LCA: 'LCA', IMAGING: 'Imagerie' };
var ORDRE_TRAINING = ['ANNALS', 'ECNI_BLANK', 'CONTEST', 'QUIZZ', 'CONFTUBE', 'MASTERCLASS', 'LCA', 'IMAGING', 'EASY_DP', 'MINI_DP', 'KFP', 'TCS'];
var TRANCHE_TRAINING = 60;
function nomType(t) { return TYPES_ANNALES[t] || t; }
function dansTraining(t) { return ORDRE_TRAINING.indexOf(t) >= 0; }
// La classe de teinte d'un type : elle pose --ep, que les points, fonds et filets mélangent.
function classeType(t) { return 'ep-' + String(t || '').toLowerCase().replace(/_/g, '-'); }
function pointType(t) { return html`<span class="trn-point ${classeType(t)}" aria-hidden="true"></span>`; }
function puceType(t) { return html`<span class="puce trn-puce ${classeType(t)}">${pointType(t)}${nomType(t)}</span>`; }

// Ce que tu as déjà fait, relu dans le journal : une réponse marquée src:'annale' porte l'identifiant de son sujet.
// Le dernier score de chaque question compte ; la note du sujet en est la moyenne.
function suiviAnnales() {
    var par = {};
    (state.learning.log || []).forEach(function(e) {
        if (e.src !== 'annale' || !e.ex || typeof e.s !== 'number') return;
        (par[e.ex] = par[e.ex] || { q: {} }).q[e.q] = e.s;
    });
    Object.keys(par).forEach(function(k) {
        var v = Object.keys(par[k].q).map(function(q) { return par[k].q[q]; });
        par[k].n = v.length; par[k].score = v.reduce(function(a, b) { return a + b; }, 0) / v.length;
    });
    return par;
}
// EDN.fr date « 2026-09-11 14:00:00+00 » : un fuseau sans minutes, que Date ne sait pas lire tel quel.
function dateSujet(d) {
    if (!d) return null;
    var x = new Date(String(d).trim().replace(' ', 'T').replace(/([+-]\d\d)$/, '$1:00'));
    return isNaN(x) ? null : x;
}
function dateCourte(d) { var x = dateSujet(d); return x ? x.toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' }) : ''; }
// La matière d'un sujet : celle de chacun de ses items dans le catalogue d'Ednplanner. Le catalogue range pourtant une
// quarantaine d'items en « Autre » (addictologie, douleur, soins palliatifs…) : pour ceux-là, et quand aucun item n'y
// figure, on prend les spécialités qu'EDN.fr donne au sujet, qui sont exactes — sous le nom du catalogue quand il
// y en a un, pour qu'une même matière ne fasse pas deux groupes.
var MATIERES_EDN = {
    'Cardio-vasculaire': 'Cardiologie', 'Chirurgie digestive': 'Chirurgie dig.', 'Gynécologie-obstétrique': 'Gynéco-Obstétrique',
    'Hépato-gastro-Enterologie': 'HGE', 'Médecine physique et réadaptation': 'MPR', 'Orthopédie': 'Orthopédie-Trauma.',
    'Santé Publique': 'Santé publique', 'Urgences médicales': 'Réa-Urgences', 'Non classé': '',
};
function matieresDuSujet(s) {
    var i = I(), vues = {}, flou = false;
    (s.items || []).forEach(function(n) {
        var m = i.specOf ? i.specOf(String(n)) : null;
        if (m && m !== 'Autre') vues[m] = 1; else flou = true;
    });
    if (flou || !Object.keys(vues).length) (s.matieres || []).forEach(function(m) { m = m in MATIERES_EDN ? MATIERES_EDN[m] : m; if (m) vues[m] = 1; });
    var liste = Object.keys(vues);
    return liste.length ? liste : ['Autre'];
}
function sujetsFiltres() {
    var a = state.qcm.annales || {}, ix = a.index || {};
    var item = String(a.item || '').trim(), mat = a.matiere || '';
    return (ix.sujets || []).filter(function(s) {
        if (!dansTraining(s.type)) return false;
        if (a.type && a.type !== '*' && s.type !== a.type) return false;
        if (item && !(s.items || []).some(function(n) { return String(n) === item; })) return false;
        if (mat && matieresDuSujet(s).indexOf(mat) < 0) return false;
        return true;
    });
}
function parDate(x, y) {
    var dx = dateSujet(x.publie_le), dy = dateSujet(y.publie_le);
    return (dy ? dy.getTime() : 0) - (dx ? dx.getTime() : 0);
}
function puceFait(f) {
    if (!f) return '';
    var classe = f.score >= 0.75 ? 'puce-ok' : (f.score >= 0.5 ? 'puce-warn' : 'puce-danger');
    return html`<span class="puce ${classe} num" title="${pluriel(f.n, 'question répondue', 'questions répondues')} — moyenne de tes dernières réponses">fait · ${pct(f.score)} %</span>`;
}

// Une ligne de registre : le point du type, le titre, et dessous ce qu'il faut pour choisir.
function ligneSujet(s) {
    var a = state.qcm.annales || {}, f = (a._suivi || {})[s.id], items = s.items || [];
    var itemsTexte = items.length ? (items.length > 6 ? 'items ' + items.slice(0, 6).join(', ') + '… (' + items.length + ')' : (items.length > 1 ? 'items ' : 'item ') + items.join(', ')) : '';
    return html`<li><button type="button" class="trn-ligne ${classeType(s.type)}" data-action="annalesSujet" data-args="${attr([s.type, s.id])}">
        ${pointType(s.type)}
        <span class="trn-ligne-corps">
            <span class="trn-ligne-titre">${s.titre || s.id}${s.theme ? html` <span class="trn-ligne-theme">— ${s.theme}</span>` : ''}</span>
            <span class="trn-ligne-meta num">${a.type === '*' ? html`<span class="trn-ligne-type">${nomType(s.type)}</span>` : ''}<span>${pluriel(s.nb_questions || 0, 'question', 'questions')}</span>${itemsTexte ? html`<span>${itemsTexte}</span>` : ''}${s.publie_le && dateCourte(s.publie_le) ? html`<span>${dateCourte(s.publie_le)}</span>` : ''}${s.video ? html`<span class="trn-ligne-video">${ico('play', { taille: 12 })}vidéo</span>` : ''}</span>
        </span>
        <span class="trn-ligne-fin">${puceFait(f)}${ico('caret-right', { taille: 16 })}</span>
    </button></li>`;
}

// La liste, rangée selon « Classer par ». Par matière ou par item, des groupes repliables : 932 sujets ne tiennent
// pas dans une page, et un groupe fermé dit déjà combien il en contient et combien sont faits. Un sujet qui couvre
// plusieurs matières (ou items) apparaît sous chacune : on révise une spécialité avec tous les sujets qui en parlent.
// Seuls les groupes ouverts dessinent leurs lignes. Par date, une liste unique montée par tranches de soixante.
function listeAnnales() {
    var a = state.qcm.annales || {}, sujets = sujetsFiltres(), classer = a.classer || 'matiere', suivi = a._suivi || {};
    if (!sujets.length) return html`<p class="trn-vide">Aucun sujet ne correspond à ces filtres.</p>`;
    if (classer === 'date') {
        var tries = sujets.slice().sort(parDate), nb = Math.min(tries.length, a.nbDate || TRANCHE_TRAINING);
        return html`<ul class="trn-registre">${tries.slice(0, nb).map(ligneSujet)}</ul>
            ${nb < tries.length ? html`<div class="trn-pied"><span class="num">${nb} des ${tries.length} sujets affichés</span>
                <button type="button" class="btn btn-fantome btn-sm" data-action="annalesPlus">Afficher ${Math.min(TRANCHE_TRAINING, tries.length - nb)} de plus</button></div>` : ''}`;
    }
    var groupes = {};
    sujets.forEach(function(s) {
        var cles = classer === 'item' ? (s.items || []).map(String) : matieresDuSujet(s);
        if (!cles.length) cles = ['—'];
        cles.forEach(function(k) { (groupes[k] = groupes[k] || []).push(s); });
    });
    var cles = Object.keys(groupes).sort(function(x, y) {
        if (classer === 'item') return (parseInt(x, 10) || 9999) - (parseInt(y, 10) || 9999);
        if (x === 'Autre') return 1; if (y === 'Autre') return -1;
        return x.localeCompare(y, 'fr');
    });
    // Déplié d'office quand la liste est déjà courte ou qu'un filtre la resserre ; ce qu'on a ouvert ou fermé soi-même
    // l'emporte toujours.
    var ouverts = a.ouverts || {}, toutOuvert = cles.length <= 3 || !!a.item || !!a.matiere;
    var i = I();
    return html`<div class="trn-groupes">${cles.map(function(k) {
        var liste = groupes[k], cle = classer + ':' + k, ouvert = cle in ouverts ? ouverts[cle] : toutOuvert;
        var faits = liste.filter(function(s) { return suivi[s.id]; }).length;
        var titre = classer === 'item'
            ? (k === '—' ? 'Sans item rattaché' : html`<span class="num trn-groupe-num">${k}</span><span class="trn-groupe-nom">${i.itemTitle ? i.itemTitle(k) : ''}</span>`)
            : html`<span class="puce ${window.getSpecialtyStyle ? window.getSpecialtyStyle(k) : 'mat'} trn-groupe-mat">${k}</span>`;
        return html`<section class="trn-groupe${ouvert ? ' trn-groupe--ouvert' : ''}">
            <h3 class="trn-groupe-tete"><button type="button" class="trn-groupe-bouton" aria-expanded="${ouvert ? 'true' : 'false'}" data-action="annalesGroupe" data-args="${attr([classer + ':' + k])}">
                ${ico('caret-right', { taille: 16, classe: 'trn-groupe-caret' })}
                <span class="trn-groupe-titre">${titre}</span>
                <span class="trn-groupe-compte num">${pluriel(liste.length, 'sujet', 'sujets')}${faits ? html` · <span class="trn-groupe-faits">${faits} fait${faits > 1 ? 's' : ''}</span>` : ''}</span>
            </button></h3>
            ${ouvert ? html`<ul class="trn-registre">${liste.slice().sort(parDate).map(ligneSujet)}</ul>` : ''}
        </section>`;
    })}</div>`;
}
function majListeAnnales(focusCle) {
    var el = document.getElementById('annales-liste');
    if (el) el.innerHTML = String(listeAnnales());
    var nb = document.getElementById('annales-nb');
    if (nb) nb.textContent = pluriel(sujetsFiltres().length, 'sujet', 'sujets');
    var b = focusCle && boutonGroupe(focusCle);
    if (b) b.focus();
}
// Filtres et groupes : on ne redessine QUE la liste, pour que le champ en cours de saisie garde son focus.
// Choisir une section ou un classement redessine toute la page : le focus revient sur le bouton choisi, et la
// pilule active de la bande (téléphone) est ramenée dans le champ au lieu de repartir au début.
// Le rendu de la coquille peut repasser à l'image suivante : on recommence une fois après lui.
window.qcmTrainingRefocus = function(action, arg, relance) {
    if (!relance) requestAnimationFrame(function() { requestAnimationFrame(function() { window.qcmTrainingRefocus(action, arg, true); }); });
    var sel = '[data-action="' + action + '"]', b = Array.prototype.find.call(document.querySelectorAll(sel), function(x) { return x.getAttribute('data-args') === JSON.stringify([arg]); });
    if (b && (!relance || document.activeElement === document.body)) b.focus({ preventScroll: true });
    var actif = document.querySelector('.trn-nav-entree[aria-current]');
    if (actif && actif.scrollIntoView) actif.scrollIntoView({ block: 'nearest', inline: 'nearest' });
};
actions.annalesClasser = function(c) { state.qcm.annales.classer = c; state.qcm.annales.nbDate = TRANCHE_TRAINING; window.renderContent(); window.qcmTrainingRefocus('annalesClasser', c); };
actions.annalesMatiere = function(el) { state.qcm.annales.matiere = el && el.value || ''; state.qcm.annales.nbDate = TRANCHE_TRAINING; majListeAnnales(); };
actions.annalesItem = function(el) { state.qcm.annales.item = (el && el.value || '').replace(/\D/g, ''); state.qcm.annales.nbDate = TRANCHE_TRAINING; majListeAnnales(); };
actions.annalesPlus = function() { var a = state.qcm.annales; a.nbDate = (a.nbDate || TRANCHE_TRAINING) + TRANCHE_TRAINING; majListeAnnales(); };
function boutonGroupe(cle) {
    return Array.prototype.find.call(document.querySelectorAll('#annales-liste .trn-groupe-bouton'), function(x) { return x.getAttribute('data-args') === JSON.stringify([cle]); });
}
actions.annalesGroupe = function(cle) {
    var a = state.qcm.annales, b = boutonGroupe(cle); a.ouverts = a.ouverts || {};
    a.ouverts[cle] = b ? b.getAttribute('aria-expanded') !== 'true' : !a.ouverts[cle];
    majListeAnnales(cle);
};

// La navigation entre sous-sections : une colonne collante sur grand écran, une bande qui défile au téléphone.
function navTraining(sujets, a) {
    var parType = {}, faitsType = {};
    sujets.forEach(function(s) { parType[s.type] = (parType[s.type] || 0) + 1; if (a._suivi[s.id]) faitsType[s.type] = (faitsType[s.type] || 0) + 1; });
    var entree = function(t, titre, nb, faits) {
        var actif = a.type === t;
        return html`<li><button type="button" class="trn-nav-entree${t === '*' ? '' : ' ' + classeType(t)}${nb ? '' : ' trn-nav-entree--vide'}"${actif ? html` aria-current="true"` : ''} data-action="annalesType" data-args="${attr([t])}">
            ${t === '*' ? html`<span class="trn-point trn-point--tout" aria-hidden="true"></span>` : pointType(t)}
            <span class="trn-nav-nom">${titre}</span>
            <span class="trn-nav-compte num">${nb || '—'}${faits ? html`<span class="trn-nav-faits"> · ${faits} fait${faits > 1 ? 's' : ''}</span>` : ''}</span>
        </button></li>`;
    };
    var totalFaits = sujets.filter(function(s) { return a._suivi[s.id]; }).length;
    return html`<nav class="trn-nav" aria-label="Sections du training"><ul>
        ${entree('*', 'Tout', sujets.length, totalFaits)}
        ${ORDRE_TRAINING.map(function(t) { return entree(t, nomType(t), parType[t] || 0, faitsType[t] || 0); })}
    </ul></nav>`;
}

// Le bandeau de la sous-section choisie : sa teinte en fond léger, son nom, ce qu'on y trouve, et l'avancement.
function bandeauSection(sujets, a) {
    var t = a.type || '*', dans = t === '*' ? sujets : sujets.filter(function(s) { return s.type === t; });
    var faits = dans.filter(function(s) { return a._suivi[s.id]; }).length;
    var questions = dans.reduce(function(n, s) { return n + (s.nb_questions || 0); }, 0);
    var part = dans.length ? faits / dans.length : 0;
    return html`<header class="trn-bandeau${t === '*' ? '' : ' ' + classeType(t)}">
        <div class="trn-bandeau-texte">
            <h2>${t === '*' ? 'Tous les sujets' : html`${pointType(t)}${nomType(t)}`}</h2>
            <p>${t === '*' ? 'Toutes les sections réunies, pour réviser une matière ou un item quel que soit le format.' : (DESCRIPTIONS_TYPES[t] || '')}</p>
        </div>
        ${dans.length ? html`<div class="trn-bandeau-chiffres">
            <p class="num"><strong>${dans.length}</strong> ${dans.length > 1 ? 'sujets' : 'sujet'} · ${questions.toLocaleString('fr-FR')} questions</p>
            <div class="trn-bandeau-avance">
                <span class="jauge-piste trn-jauge" role="img" aria-label="${faits} sujets faits sur ${dans.length}"><span class="jauge-val" style="transform:scaleX(${faits ? Math.max(0.02, part) : 0})"></span></span>
                <span class="num">${faits} fait${faits > 1 ? 's' : ''} sur ${dans.length}</span>
            </div>
        </div>` : ''}
    </header>`;
}

function vueTraining() {
    var a = state.qcm.annales;
    if (!a) {
        // Le chargement se lance hors du dessin en cours : il redessine lui-même à l'arrivée de l'index.
        if (!state.qcm._annalesDemandees) { state.qcm._annalesDemandees = true; setTimeout(function() { window.actions.annalesCharger(); }, 0); }
        a = { chargement: true };
    }
    var ix = a.index;
    var sujets = ((ix && ix.sujets) || []).filter(function(s) { return dansTraining(s.type); });
    a._suivi = a.chargement || a.erreur ? {} : suiviAnnales();
    var totalQ = sujets.reduce(function(n, s) { return n + (s.nb_questions || 0); }, 0);
    var faitsTotal = sujets.filter(function(s) { return a._suivi[s.id]; }).length;
    var tete = html`<header class="qcm-entete trn-entete">
        <div class="qcm-entete-texte">
            <h1>Training</h1>
            <p class="qcm-resume num">${sujets.length ? html`${sujets.length} sujets · ${totalQ.toLocaleString('fr-FR')} questions · ${faitsTotal} fait${faitsTotal > 1 ? 's' : ''} — en épreuve, la correction arrive à la fin.` : 'Les sujets d\'EDN.fr, en épreuve : la correction arrive à la fin.'}</p>
        </div>
        <span class="puce puce-sans-point trn-prive" title="Ces sujets viennent de ton abonnement EDN.fr : ils ne sont servis à aucun autre compte.">${ico('lock-simple', { taille: 14 })}Visible par toi seul</span>
    </header>${noticeHtml()}`;
    if (a.chargement) return html`${tete}<div class="squelettes"><div class="squelette squelette-bloc"></div><div class="squelette squelette-bloc"></div></div>`;
    if (a.erreur) return html`${tete}<div class="qcm-alerte" role="alert">${ico('warning-circle', { taille: 20 })}<div><p>${a.erreur}</p></div></div>`;
    if (!sujets.length) {
        return html`${tete}<div class="vide trn-vide-page">${ico('graduation-cap')}<h2>Aucun sujet déposé</h2>
            <p>Récupère les sujets avec <code>edn_annales_scraper.py</code> sur ta machine (dépôt <code>qcm</code>, branche <code>annales</code>),
            puis dépose le dossier <code>annales_edn/</code> sur le serveur sous <code>edn_data/annales/</code>.</p></div>`;
    }
    var type = a.type || '*', vide = type !== '*' && !sujets.some(function(s) { return s.type === type; });
    var matieres = {};
    sujets.forEach(function(s) { if (type === '*' || s.type === type) matieresDuSujet(s).forEach(function(m) { matieres[m] = 1; }); });
    var classer = a.classer || 'matiere';
    return html`${tete}
        <div class="trn">
            ${navTraining(sujets, a)}
            <div class="trn-corps">
                ${bandeauSection(sujets, a)}
                ${vide ? html`<div class="trn-vide-section"><p>Aucun sujet de cette section n'est encore déposé.</p>
                    <p class="trn-aide">Récupère-les avec <code>--types ${type}</code>, puis renvoie le dossier sur le serveur.</p></div>` : html`
                <div class="trn-outils" role="search" aria-label="Filtrer les sujets">
                    <div class="segments trn-classer" role="group" aria-label="Classer par">
                        ${[['matiere', 'Matière'], ['item', 'Item'], ['date', 'Date']].map(function(o) {
                            return html`<button type="button" aria-pressed="${classer === o[0] ? 'true' : 'false'}" data-action="annalesClasser" data-args="${attr([o[0]])}">${o[1]}</button>`;
                        })}
                    </div>
                    <span class="select-enveloppe qcm-select trn-matiere"><label for="ann-matiere" class="sr-only">Matière</label>
                        <select id="ann-matiere" class="select saisie" data-change-action="annalesMatiere">
                            <option value="">Toutes les matières</option>
                            ${Object.keys(matieres).sort(function(x, y) { return x.localeCompare(y, 'fr'); }).map(function(m) {
                                return html`<option value="${m}"${a.matiere === m ? html` selected` : ''}>${m}</option>`;
                            })}
                        </select></span>
                    <label class="trn-item"><span class="sr-only">Numéro d'item</span>
                        <input id="ann-item" class="saisie" type="text" inputmode="numeric" placeholder="N° d'item" value="${a.item || ''}" data-input-action="annalesItem" autocomplete="off"></label>
                    <span id="annales-nb" class="num trn-nb" role="status">${pluriel(sujetsFiltres().length, 'sujet', 'sujets')}</span>
                </div>
                <div id="annales-liste" class="trn-liste">${listeAnnales()}</div>`}
            </div>
        </div>`;
}
// Ce que la page Training affiche selon la vue : la liste, un sujet, ou l'épreuve d'une annale en cours.
function vueTrainingRoute() {
    var v = state.qcm.view, s = state.qcm.session;
    if (v === 'annaleSujet') return vueAnnaleSujet();
    if (v === 'session' && s && s.annale) return vueSeance();
    return vueTraining();
}

function vueAnnaleSujet() {
    var a = state.qcm.annaleSujet || {}, i = I(), s = a.sujet;
    var retour = html`<button type="button" class="qcm-retour" data-action="annalesRetour">${ico('arrow-left', { taille: 16 })}Training</button>`;
    if (a.chargement) return html`<div class="qcm-annale">${retour}<div class="squelettes"><div class="squelette squelette-titre"></div><div class="squelette squelette-bloc"></div></div></div>`;
    if (!s) return html`<div class="qcm-annale">${retour}<div class="qcm-alerte" role="alert">${ico('warning-circle', { taille: 20 })}<div><p>${a.erreur || 'Sujet introuvable.'}</p></div></div></div>`;
    var type = s.type || a.type, dossiers = [], nbIso = 0, nbQ = 0;
    (s.contenus || []).forEach(function(b, ib) {
        (b.dossiers || []).forEach(function(d, id) { dossiers.push({ cle: ib + ':' + id, d: d }); nbQ += (d.questions || []).length; });
        nbIso += (b.questions_isolees || []).length;
    });
    nbQ += nbIso;
    var mats = matieresDuSujet(s), items = s.items || [], f = suiviAnnales()[s.id];
    var meta = [pluriel(nbQ, 'question', 'questions')];
    if (dossiers.length) meta.push(pluriel(dossiers.length, 'dossier', 'dossiers'));
    if (nbIso && dossiers.length) meta.push(pluriel(nbIso, 'question isolée', 'questions isolées'));
    if (s.r2c) meta.push('R2C');
    if (dateCourte(s.publie_le)) meta.push(dateCourte(s.publie_le));
    return html`<div class="qcm-annale ${classeType(type)}">
        ${retour}
        <header class="trn-sujet-tete">
            <h1>${s.titre}</h1>
            <p class="trn-sujet-meta num">${puceType(type)}<span>${meta.join(' · ')}</span></p>
            ${s.theme ? html`<p class="trn-sujet-theme">${s.theme}</p>` : ''}
            <div class="trn-sujet-lancer">
                <button type="button" class="btn btn-primaire" data-action="annalesLancer" data-args="${attr([''])}"${nbQ ? '' : html` disabled`}>${ico('lightning', { taille: 18 })}${f ? 'Refaire l\'épreuve' : 'Commencer l\'épreuve'}</button>
                ${f ? puceFait(f) : ''}
                <span class="trn-aide">Tu réponds à tout, dans l'ordre du sujet. La correction arrive à la fin, question par question.</span>
            </div>
        </header>
        ${noticeHtml()}
        ${s.video && (s.video.cours || s.video.correction) ? html`<section class="trn-section">
            <h2>Vidéos</h2>
            <div class="trn-videos">
                ${lienVideo(s.video.cours, 'Vidéo du cours', 'btn-secondaire')}
                ${lienVideo(s.video.correction, 'Vidéo de la correction', 'btn-fantome')}
            </div>
            <p class="trn-aide">S'ouvrent sur YouTube. Garde la correction pour après l'épreuve.</p>
        </section>` : ''}
        <section class="trn-section">
            <h2>Ce que couvre le sujet</h2>
            ${(function() {
                // Une épreuve complète touche presque toutes les matières : au-delà de douze, on les replie.
                var puces = html`<div class="trn-matieres">${mats.map(function(m) { return html`<span class="puce ${window.getSpecialtyStyle ? window.getSpecialtyStyle(m) : 'mat'}">${m}</span>`; })}</div>`;
                return mats.length > 12 ? html`<details class="trn-matieres-plus"><summary>${mats.length} matières</summary>${puces}</details>` : puces;
            })()}
            ${items.length ? (function() {
                var liste = html`<ul class="trn-items">${items.map(function(n) {
                    return html`<li><button type="button" class="qcm-lien trn-item-lien" data-action="qcmOpenItem" data-args="${attr([String(n), '', '', ''])}"><span class="num">${n}</span> ${i.itemTitle ? i.itemTitle(String(n)) : ''}</button></li>`;
                })}</ul>`;
                return items.length > 8 ? html`<details class="trn-items-plus"><summary>${pluriel(items.length, 'item couvert', 'items couverts')}</summary>${liste}</details>` : liste;
            })() : ''}
        </section>
        ${dossiers.length ? html`<section class="trn-section">
            <h2>Dossiers</h2>
            <ol class="trn-dossiers">${dossiers.map(function(x, k) {
                return html`<li class="trn-dossier">
                    <span class="trn-dossier-num num">${k + 1}</span>
                    <span class="trn-dossier-corps">
                        <span class="trn-dossier-titre">${x.d.titre || 'Dossier'}</span>
                        <span class="trn-dossier-meta num">${x.d.type ? (TYPES_DOSSIER[x.d.type] || x.d.type) + ' · ' : ''}${pluriel((x.d.questions || []).length, 'question', 'questions')}</span>
                    </span>
                    <button type="button" class="btn btn-fantome btn-sm" data-action="annalesLancer" data-args="${attr([x.cle])}">Ce dossier seul</button>
                </li>`;
            })}</ol>
            ${nbIso ? html`<p class="trn-aide num">Puis ${pluriel(nbIso, 'question isolée', 'questions isolées')}, à la suite des dossiers.</p>` : ''}
        </section>` : ''}
    </div>`;
}

// Les vidéos YouTube d'un Conftube (toutes en ont deux) : le cours, qu'on regarde avant de répondre, et la
// correction, pour après. Elles s'ouvrent dans un autre onglet : la séance reste où elle en est.
function lienVideo(url, texte, classe) {
    return url ? html`<a class="btn ${classe} btn-sm" href="${url}" target="_blank" rel="noopener noreferrer">${ico('play', { taille: 16 })}${texte}</a>` : '';
}

// En épreuve : le sujet, le dossier en cours, et combien de questions sont faites — pas de score avant la fin.
// Un Conftube garde sous la main la vidéo du cours ; celle de la correction attend la fin.
function enteteSeanceAnnale(s) {
    var q = s.list[s.idx] || {}, d = q._dossier;
    return html`<header id="qcm-seance-tete" class="qcm-seance-tete">
        <div class="qcm-seance-qui">
            <p class="qcm-seance-mode">${pointType(s.annale.type)}${nomType(s.annale.type)} · épreuve, correction à la fin${d ? html` · <span class="num">dossier ${d.rang}/${d.total}</span>` : (s.finished ? '' : ' · question isolée')}</p>
            <h1>${s.annale.titre}</h1>
            ${s.annale.video && s.annale.video.cours && !s.finished ? html`<div class="trn-seance-video">${lienVideo(s.annale.video.cours, 'Vidéo du cours', 'btn-fantome')}</div>` : ''}
        </div>
        <div class="qcm-seance-etat">
            ${avancement(s.results.length, s.list.length, { label: 'Questions répondues', texte: s.results.length + ' / ' + s.list.length })}
        </div>
        <button type="button" class="ico-btn" data-action="qcmQuit" aria-label="Quitter l'épreuve" title="Quitter l'épreuve — les réponses déjà données restent comptées">${ico('x')}</button>
    </header>`;
}

// Les images jointes au cas d'un dossier (radio, ECG, tableau de biologie…) : EDN.fr les sert à côté de l'énoncé,
// pas dedans. Celles que le texte montre déjà ne sont pas répétées ; un fichier qui n'est pas une image devient un lien.
function piecesDossier(d) {
    var i = I(), dedans = d.enonce_html || '', vues = {};
    var imgs = (d.images || []).filter(function(im) {
        if (!im.fichier || vues[im.fichier] || dedans.indexOf(im.fichier) >= 0) return false;
        return (vues[im.fichier] = /\.(png|jpe?g|gif|webp|svg|bmp|avif)$/i.test(im.fichier));
    });
    var autres = (d.contenus_enonce || []).filter(function(c) { var n = String(c.fichier || '').split('/').pop(); return n && !vues[n] && dedans.indexOf(n) < 0; });
    if (!imgs.length && !autres.length) return '';
    return html`${imgs.length ? html`<div class="qcm-images">${imgs.map(function(im) {
        return html`<a class="qcm-image" href="${i.imgUrl(im.fichier)}" target="_blank" rel="noopener" title="Ouvrir l'image en grand">
            <img src="${i.imgUrl(im.fichier)}" alt="" data-qcm-img="${im.fichier}" loading="lazy"></a>`;
    })}</div>` : ''}${autres.length ? html`<ul class="qcm-pj">${autres.map(function(c) {
        return html`<li><a href="${i.imgUrl(c.fichier)}" target="_blank" rel="noopener">${ico('file-text', { taille: 16 })}${c.titre && c.titre !== 'x' ? c.titre : 'Pièce jointe du dossier'}</a></li>`;
    })}</ul>` : ''}`;
}

// Le cas clinique du dossier, relu au-dessus de chaque question : dans un dossier progressif, l'énoncé commun est
// la moitié de la question. Ouvert par défaut, repliable, sur un fond à peine teinté du type d'épreuve ; ses images
// jointes y sont à chaque question.
function dossierAnnale(q) {
    var d = q && q._dossier; if (!d) return '';
    var type = state.qcm.session && state.qcm.session.annale ? state.qcm.session.annale.type : '';
    return html`<details class="trn-cas ${classeType(type)}" open>
        <summary><span class="num">Dossier ${d.rang}/${d.total}</span> · ${d.titre || ''}${q._rangDossier ? html` <span class="num trn-cas-q">— question ${q._rangDossier}/${q._nbDossier}</span>` : ''}</summary>
        ${d.enonce_html ? html`<div class="qcm-rich trn-cas-texte">${window.brut(I().cleanHtml ? I().cleanHtml(d.enonce_html) : '')}</div>` : ''}
        ${piecesDossier(d)}
        ${precedentesDossier(q)}
    </details>`;
}

// Les questions du dossier déjà passées dans cette séance, dans l'ordre : leurs énoncés portent souvent ce que la
// question en cours suppose connu. Ouvertes par défaut ; leurs propositions restent repliées.
function precedentesDossier(q) {
    var s = state.qcm.session, d = q._dossier; if (!s || !d) return '';
    var meme = function(x) { return x && x._dossier && x._dossier.rang === d.rang && x._dossier.titre === d.titre; };
    var avant = s.list.slice(0, s.idx).filter(meme);
    if (!avant.length) return '';
    var resultat = function(x) { for (var k = s.results.length - 1; k >= 0; k--) if (s.results[k].q && s.results[k].q.id === x.id) return s.results[k]; return null; };
    return html`<details class="trn-precedentes" open>
        <summary>${pluriel(avant.length, 'question précédente', 'questions précédentes')} du dossier</summary>
        <ol class="trn-prec-ol">${avant.map(function(x) { return questionDejaPassee(x, resultat(x), x._rangDossier || ''); })}</ol>
    </details>`;
}

// Fin d'épreuve : la correction entière, question par question, rangée sous son dossier.
function finAnnale(s, fautes) {
    var avant = null, k = 0, type = s.annale ? s.annale.type : '';
    var revue = s.results.map(function(r) {
        var q = r.q, d = q._dossier, cle = d ? d.titre + '|' + d.rang : '—isolées—', entete = '';
        if (cle !== avant) {
            entete = d ? html`<h3 class="trn-revue-dossier"><span class="num">Dossier ${d.rang}</span> · ${d.titre || ''}</h3>
                ${d.enonce_html || (d.images || []).length ? html`<details class="trn-cas ${classeType(type)}"><summary>Relire le cas</summary><div class="qcm-rich trn-cas-texte">${window.brut(I().cleanHtml ? I().cleanHtml(d.enonce_html || '') : '')}</div>${piecesDossier(d)}</details>` : ''}`
                : html`<h3 class="trn-revue-dossier">Questions isolées</h3>`;
            avant = cle;
        }
        k++;
        return html`${entete}${revueQuestion(q, r, k)}`;
    });
    var v = s.annale && s.annale.video;
    return html`<section class="ann-revue" aria-label="Correction de l'épreuve">
        <h2>Correction de l'épreuve</h2>
        ${v && (v.correction || v.cours) ? html`<div class="trn-videos trn-revue-videos">${lienVideo(v.correction, 'Vidéo de la correction', 'btn-secondaire')}${lienVideo(v.cours, 'Vidéo du cours', 'btn-fantome')}</div>` : ''}
        ${revue}
    </section>
    <div class="qcm-fin-suite">
        ${fautes ? html`<button type="button" class="btn btn-primaire" data-action="qcmRestartErrors">${ico('arrow-counter-clockwise', { taille: 18 })}Refaire ${pluriel(fautes, 'erreur', 'erreurs')}</button>` : ''}
        <button type="button" class="btn ${fautes ? 'btn-secondaire' : 'btn-primaire'}" data-action="qcmQuit">${ico('arrow-left', { taille: 18 })}Retour au sujet</button>
    </div>`;
}

function vue(v) {
    if (v === 'item') return vueItem();
    if (v === 'session') return vueSeance();
    if (v === 'stats') return vueStats();
    if (v === 'flash') return vueFlash();
    if (v === 'recall') return vueRecall();
    if (v === 'soirFin') return vueSoirFin();
    if (v === 'annales' || v === 'annaleSujet') return vueRegistre();   // vivent sur la page Training
    if (v === 'cours') return vueCours();
    return vueRegistre();
}

export function renderQcmTab() {
    var Q = state.qcm;
    if (Q.reprise === 'attente') {
        return String(html`<div id="qcm-root" class="qcm"><div class="vide">${ico('circle-notch', { taille: 32, classe: 'qcm-tourne' })}
            <h2>Reprise de ta séance de QCM</h2><p>Tes réponses ont été gardées sur cet appareil : la séance repart là où tu l'avais laissée.</p></div></div>`);
    }
    var corps;
    try {
        if (state.activeTab === 'training') corps = vueTrainingRoute();
        // Une épreuve d'annale en cours appartient à Training : sur QCM, on montre le registre sans la perdre.
        else corps = (Q.view === 'session' && Q.session && Q.session.annale) ? vueRegistre() : vue(Q.view);
    }
    catch (e) {
        if (window.signalerErreur) window.signalerErreur(e, 'qcm');
        else console.error(e);
        corps = html`<div class="qcm-alerte" role="alert">${ico('warning-circle', { taille: 20 })}<div><p>L'affichage de cette vue a échoué : ${e.message}</p>
            <p class="qcm-alerte-aide"><button type="button" class="qcm-lien" data-action="qcmBackToList">Revenir au registre</button></p></div></div>`;
    }
    return String(html`<div id="qcm-root" class="qcm fade-in">${bandeauAbonnement()}${corps}</div>`);
}

export function afterRenderQcm() {
    var v = state.qcm.view;
    if (v === 'list' || v === 'quiz') {
        window.loadQcmIndex(function() { renderQcmListe(); });
        if (state.qcm.index) renderQcmListe();
    } else if (!state.qcm.index) window.loadQcmIndex();
    if (v === 'session') mesurerImageZone();
}

// ================================================================= gestes et mises à jour ciblées

// Cocher une proposition (ou poser un pointage) ne réécrit rien : on retourne les classes et les cases, et on
// ouvre ou ferme « Valider ». Réécrire la liste ferait perdre le focus de la case qu'on vient de cocher — au
// clavier, on se retrouvait renvoyé en haut de la page à chaque choix.
window.qcmMajReponse = function() {
    var i = I(), s = state.qcm.session, q = i.curQ(); if (!s || !q) return;
    var props = document.getElementById('qcm-props');
    if (props) {
        if (s.revealed) props.outerHTML = String(propositionsHtml(q, s));
        else {
            var multiple = i.isMany(q);
            // QRP : une fois le nombre annoncé atteint, les cases restantes se bloquent — et se libèrent dès qu'on en décoche une.
            var plein = i.isDefined && i.isDefined(q) && Object.keys(s.ans || {}).filter(function(k) { return s.ans[k]; }).length >= i.nbAttendu(q);
            Array.prototype.forEach.call(props.querySelectorAll('.qcm-prop'), function(el) {
                var id = el.getAttribute('data-prop');
                var choisie = multiple ? !!(s.ans && s.ans[id]) : s.ans === id;
                el.classList.toggle('qcm-prop--choisie', choisie);
                el.classList.toggle('qcm-prop--bloquee', plein && !choisie);
                var ctrl = el.querySelector('.qcm-prop-controle');
                if (ctrl) { ctrl.checked = choisie; ctrl.disabled = !!(plein && !choisie); }
            });
        }
    }
    var calque = document.getElementById('qcm-zone-calque');
    if (calque) {
        calque.innerHTML = String(zoneCalque(q, s));
        var aide = document.getElementById('qcm-zone-aide'); if (aide) aide.textContent = zoneAide(s);
    }
    var valider = document.getElementById('qcm-valider');
    if (valider) {
        if (reponsePrete(q, s)) { valider.removeAttribute('disabled'); valider.removeAttribute('aria-disabled'); }
        else { valider.setAttribute('disabled', ''); valider.setAttribute('aria-disabled', 'true'); }
    }
};

// La correction s'ouvre SOUS la question, sans reconstruire la page : trois nœuds changent (les propositions,
// qui prennent leurs marques ; la rangée « Valider », qui s'efface ; la correction, qui se remplit), plus le
// compteur et la jauge de l'en-tête. L'énoncé, ses images et le bandeau ne bougent pas.
window.qcmMajCorrection = function() {
    var s = state.qcm.session, q = I().curQ();
    if (!s || !q || !document.getElementById('qcm-question')) { I().render(); return; }
    window.qcmMajReponse();
    var actions = document.getElementById('qcm-actions');
    if (actions) actions.hidden = true;
    var zone = document.getElementById('qcm-correction');
    if (!zone) { I().render(); return; }
    zone.outerHTML = String(correctionHtml(q, s));
    majEnteteSeance();
    var neuf = document.getElementById('qcm-correction');
    if (neuf) {
        try { neuf.focus({ preventScroll: true }); } catch (e) {}
        try { neuf.scrollIntoView({ behavior: mouvementReduit() ? 'auto' : 'smooth', block: 'nearest' }); } catch (e) {}
    }
};
function majEnteteSeance() {
    var s = state.qcm.session, tete = document.getElementById('qcm-seance-tete'); if (!s || !tete) return;
    var barre = tete.querySelector('[role="progressbar"]'), val = barre && barre.querySelector('.jauge-val');
    if (barre) {
        barre.setAttribute('aria-valuenow', String(s.results.length));
        barre.setAttribute('aria-valuetext', s.results.length + ' sur ' + s.list.length);
        if (val) val.style.transform = 'scaleX(' + (s.list.length ? s.results.length / s.list.length : 0) + ')';
    }
    var compte = document.getElementById('qcm-compteur');
    if (compte) compte.textContent = texteCompteur(s);
}

// Retourner une carte : aucune reconstruction, sinon l'animation serait coupée net. Seuls aria-pressed, les
// deux aria-hidden des faces et les deux rangées d'action changent.
window.actions.flashFlip = function() {
    var f = state.qcm.flash; if (!f) return;
    var bouton = document.querySelector('.qcm-flip-btn');
    f.flipped = !f.flipped;
    if (!bouton) { I().render(); return; }
    bouton.setAttribute('aria-pressed', f.flipped ? 'true' : 'false');
    var recto = bouton.querySelector('.qcm-face-recto'), verso = bouton.querySelector('.qcm-face-verso');
    if (recto) { if (f.flipped) recto.setAttribute('aria-hidden', 'true'); else recto.removeAttribute('aria-hidden'); }
    if (verso) { if (f.flipped) verso.removeAttribute('aria-hidden'); else verso.setAttribute('aria-hidden', 'true'); }
    var voir = document.getElementById('qcm-flash-voir'), notes = document.getElementById('qcm-flash-notes');
    if (voir) voir.hidden = f.flipped;
    if (notes) notes.hidden = !f.flipped;
    if (f.flipped && notes) { var p = notes.querySelector('button'); if (p) { try { p.focus({ preventScroll: true }); } catch (e) {} } }
};

// « Générer quand même » : l'appel à l'IA est payant et rien de neuf n'a été repéré — on le dit, on laisse choisir.
window.actions.flashGenerateQuandMeme = function() {
    window.dialogue.confirmer({
        titre: 'Générer de nouvelles cartes quand même ?',
        message: 'Aucune nouvelle erreur ni lacune depuis la dernière génération : l\'IA repartirait des mêmes points faibles, et l\'appel est facturé. Réviser le paquet existant est gratuit et suffit entre deux J.',
        ok: 'Générer quand même'
    }).then(function(oui) { if (oui) window.actions.flashGenerate(); });
};

window.actions.qcmFermerNotice = function() { state.qcm.notice = null; I().render(); };
// Déplier la liste des questions : une classe et un attribut, pas un redessin (le focus reste sur le bouton).
window.actions.qcmToggleQuestions = function(el) {
    state.qcm.showAllQuestions = !state.qcm.showAllQuestions;
    var zone = document.getElementById('qcm-questions');
    var bouton = (el && el.setAttribute) ? el : document.querySelector('.qcm-deplier');
    if (!zone || !bouton) { I().render(); return; }
    zone.hidden = !state.qcm.showAllQuestions;
    bouton.setAttribute('aria-expanded', state.qcm.showAllQuestions ? 'true' : 'false');
};
window.actions.qcmToutAfficher = function() {
    state.qcm.search = ''; state.qcm.spec = 'all'; state.qcm.onlyWithQcm = false;
    var champ = document.getElementById('qcm-search'), sel = document.getElementById('qcm-spec');
    if (champ) champ.value = ''; if (sel) sel.value = 'all';
    renderQcmListe();
    if (champ) champ.focus();
};
window.actions.qcmFermerFenetre = function() { state.modalConfig = null; if (window.renderModal) window.renderModal(); };
// Un clic sur le voile lui-même (pas sur la boîte) referme, comme le ferait le fond d'un <dialog>.
window.actions.qcmFermerVoile = function(el, ev) { if (ev && ev.target === el) window.actions.qcmFermerFenetre(); };
// La fenêtre est posée dans #modal-root en innerHTML : elle n'a ni la touche Échap ni le focus d'un <dialog>.
document.addEventListener('keydown', function(ev) {
    if (ev.key !== 'Escape') return;
    var c = state.modalConfig;
    if (c && c.type === 'livre-qcm') { ev.preventDefault(); window.actions.qcmFermerFenetre(); }
});
window.MODALES = window.MODALES || {};
window.MODALES['livre-qcm'] = function() {
    // Le focus entre dans la fenêtre au tour de boucle suivant, quand renderModal a posé le HTML.
    setTimeout(function() { var b = document.querySelector('.qcm-fenetre-boite'); if (b) { try { b.focus({ preventScroll: true }); } catch (e) {} } }, 0);
    return String(qcmLivreFenetre());
};

// Une image d'énoncé absente du serveur : le message prend la place de l'image, dans la langue du produit.
// Écoute en phase de capture : les évènements « error » d'une image ne remontent pas.
document.addEventListener('error', function(ev) {
    var el = ev.target;
    if (!el || el.tagName !== 'IMG' || !el.hasAttribute('data-qcm-img')) return;
    if (el.dataset.qcmRemplacee) return;
    el.dataset.qcmRemplacee = '1';
    var note = document.createElement('p');
    note.className = 'qcm-img-absente';
    note.textContent = 'Image absente du serveur (' + (el.getAttribute('data-qcm-img') || 'sans nom') + ') : le dossier images/ du scraper doit être déposé dans edn_data/qcm/images/.';
    var parent = el.parentNode;
    if (parent && parent.classList && parent.classList.contains('qcm-image')) parent.replaceWith(note);
    else if (el.replaceWith) el.replaceWith(note);
}, true);

// La question à pointage a besoin des dimensions natives de son image pour placer le pointage et la zone.
function mesurerImageZone() {
    var img = document.querySelector('[data-qcm-mesure]'); if (!img) return;
    var poser = function() {
        var q = I().curQ(); if (!q || !img.naturalWidth) return;
        q._nw = img.naturalWidth; q._nh = img.naturalHeight;
    };
    if (img.complete) poser(); else img.addEventListener('load', poser, { once: true });
}

// Le micro ne reste jamais ouvert quand on quitte la page ou l'onglet.
window.addEventListener('pagehide', function() { if (window.dictStopIfOn) window.dictStopIfOn(); });

window.renderQcmTab = renderQcmTab;
window.afterRenderQcm = afterRenderQcm;
window.renderQcmListe = renderQcmListe;
window.squeletteQcm = squeletteQcm;
window.qcmLivreCorps = qcmLivreCorps;
window.qcmLivreFenetre = qcmLivreFenetre;
